import { createReducer, createAction, createEntityAdapter, EntityState, Update, current } from '@reduxjs/toolkit'
import { get, pull, isArray, isString, sortBy, isNumber, isEqual } from "lodash-es";
import { assertIsDefined } from '../util/assert';
import { naturalSort } from '../util/sort';
import { AppAuthState, CloudBackend, createBackend, LogInProcessState } from './auth';
import { CustomizationValidationError, DaemonValidationError, duplicateFormValidationError, FormValidationError, generateNewFormValidationError, LoginError } from './store-errors';
import { Labeling } from './labeling';
import { LicenseStatus } from './license-status';
import { createGlobalRoiCustomization, createNewMetadataItem, createNewModelCustomizationBase, createNewModelCustomizationOutput, createNewRoiCustomization, duplicateModelCustomizationMetadataItem, duplicateRoiCustomization, generateNewId, getDefaultFmaId, getFmaId, roiCustomizationMatchesGlobalRoi, UI_ID_ATTRIBUTE } from './model-customization-helpers';
import { AeTitleRule, CustomizationObjectType, DEFAULT_INTERPRETED_TYPE, DEFAULT_MODEL_CUSTOMIZATION_NAME, DicomAttributeRule, DicomRule, FmaId, generateRandomColor, GlobalRoiCustomization, isAeTitleRule, isDicomAttributeRule, isDicomRule, isModelCustomizationBase, isModelCustomizationMetadata, isModelCustomizationOutput, isRoiCustomization, isSegmentationModel, METADATA_FILENAME_ATTRIBUTE, ModelCustomizationBase, ModelCustomizationEntities, ModelCustomizationMetadataItem, ModelCustomizationOutput, ModelCustomizationOutputEntities, ModelSelectionScope, ModelVisibility, PhysicalProperty, RoiCustomization, SegmentationModel, VALIDATION_MESSAGE_AE_TITLES_MUST_BE_UNIQUE } from './model-customization-types';

import { MVisionAppClient } from './mvision-client-list';
import { TextEditorFile } from './text-editor-file';
import { BackendValidationError, isBackendValidationError, LOC_VALUE_BODY } from '../util/errors';
import { notEmpty } from '../util/filter';
import { FullRoiDetailsId } from '../pages/customization/FullRoiDetailsModal';
import { AppVersionInfo, areVersionsSame } from './app-version-info';
import { DeploymentConfigInfo, DeploymentMode } from './deployment-config-info';
import { backendTierAppAuths } from '../web-apis/cloud-backend-auth';
import { ConfigInterface } from '../app-config';
import { ensureFilenameHasDcmExtension } from '../pages/customization/file-operations';
import { DaemonConfig } from './daemon-config';
import { FormValidationErrorType } from './form-errors';
import UserSettings, { getInitialUserSettings } from './user-settings';
import { getInitialUserAccessRights, AccessRights } from './access-rights';
import { HelpDocument } from '../i18n';

// actions & action creators

export const fetchTextEditorFiles = createAction('FETCH_TEXT_EDITOR_FILES');
export const setFoundTextEditorFilePaths = createAction('SET_FOUND_TEXT_EDITOR_FILE_PATHS',
    (foundFiles: TextEditorFile[]) => ({ payload: { foundFiles } }));
export const tryFetchTextEditorFileContents = createAction('TRY_FETCH_TEXT_EDITOR_FILE_CONTENTS',
    (file: TextEditorFile) => ({ payload: { file } }));
export const fetchTextEditorFileContents = createAction('FETCH_TEXT_EDITOR_FILE_CONTENTS',
    (filePath: string) => ({ payload: { filePath } }));
export const setTextEditorFileContents = createAction('SET_TEXT_EDITOR_FILE_CONTENTS',
    (filePath: string, contents: string) => ({ payload: { filePath, contents } }));
export const modifyTextEditorFile = createAction('MODIFY_TEXT_EDITOR_FILE',
    (filePath: string, newContents: string) => ({ payload: { filePath, newContents } }));
export const openTextEditorFileForEditing = createAction('OPEN_TEXT_EDITOR_FILE_FOR_EDITING',
    (filePath: string) => ({ payload: { filePath } }));
export const closeTextEditorFileForEditing = createAction('CLOSE_TEXT_EDITOR_FILE_FOR_EDITING',
    (filePath: string) => ({ payload: { filePath } }));
export const undoTextEditorFileChanges = createAction('UNDO_TEXT_EDITOR_FILE_CHANGES',
    (filePath: string) => ({ payload: { filePath } }));
export const saveTextEditorFileContentsStart = createAction('SAVE_TEXT_EDITOR_FILE_CONTENTS_START',
    (filePath: string, newContents: string) => ({ payload: { filePath, newContents } }));
export const saveTextEditorFileContentsFinish = createAction('SAVE_TEXT_EDITOR_FILE_CONTENTS_FINISH',
    (filePath: string, newContents: string | null, error?: string) => ({ payload: { filePath, newContents, error } }));


export const setModelCustomizations = createAction('SET_MODEL_CUSTOMIZATIONS',
    (customizations: ModelCustomizationEntities | null, error?: string) => ({ payload: { customizations, error } }));
export const setModelCustomizationObjectsAsModified = createAction('SET_MODEL_CUSTOMIZATION_OBJECTS_AS_MODIFIED',
    (updates: Partial<ModelCustomizationEntities> | null, error?: string) => ({ payload: { updates, error } }));
export const updateModelVisibility = createAction('UPDATE_MODEL_VISIBILITY', (modelId: string, visibility: ModelVisibility) => ({ payload: { modelId, visibility } }));

export const updateRoiCustomizationColor = createAction('UPDATE_ROI_CUSTOMIZATION_COLOR',
    (roiId: string, color: [number, number, number], isGlobalRoi: boolean) => ({ payload: { roiId, color, isGlobalRoi } }));
export const updateRoiCustomizationIncludedInModel = createAction('UPDATE_ROI_CUSTOMIZATION_INCLUDED_IN_MODEL',
    (roiId: string, isIncluded: boolean, isGlobalRoi: boolean) => ({ payload: { roiId, isIncluded, isGlobalRoi } }));
export const toggleAllRoisInCustomizationOutput = createAction('TOGGLE_ALL_ROIS_IN_CUSTOMIZATION_OUTPUT',
    (customizationOutputId: string, isIncluded: boolean) => ({ payload: { customizationOutputId, isIncluded } }));
export const updateRoiCustomizationName = createAction('UPDATE_ROI_CUSTOMIZATION_NAME',
    (roiId: string, name: string, isGlobalRoi: boolean) => ({ payload: { roiId, name, isGlobalRoi } }));
export const updateRoiCustomizationOperation = createAction('UPDATE_ROI_CUSTOMIZATION_OPERATION',
    (roiId: string, operation: string, isGlobalRoi: boolean) => ({ payload: { roiId, operation, isGlobalRoi } }));
export const updateRoiInterpretedType = createAction('UPDATE_ROI_INTERPRETED_TYPE',
    (roiId: string, interpretedType: string, isGlobalRoi: boolean) => ({ payload: { roiId, interpretedType, isGlobalRoi } }));
export const updateRoiPhysicalProperty = createAction('UPDATE_ROI_PHYSICAL_PROPERTY',
    (roiId: string, physicalPropertyAttribute: PhysicalProperty | undefined, physicalPropertyValue: string | undefined, isGlobalRoi: boolean) => ({ payload: { roiId, physicalPropertyAttribute, physicalPropertyValue, isGlobalRoi } }));
export const updateRoiFmaId = createAction('UPDATE_ROI_FMA_ID',
    (roiId: string, fmaId: Partial<FmaId>, isGlobalRoi: boolean) => ({ payload: { roiId, fmaId, isGlobalRoi } }));
export const copyRoiFmaIds = createAction('COPY_ROI_FMA_IDS',
    (fmaIdAttributes: Partial<FmaId>) => ({ payload: { fmaIdAttributes } }));
export const copyRoiFmaIdsOutput = createAction('COPY_ROI_FMA_IDS_OUTPUT',
    (fmaIdAttributes: Partial<FmaId>) => ({ payload: { fmaIdAttributes } }));
export const removeScrollToViewFromRoiCustomization = createAction('REMOVE_SCROLL_TO_VIEW_FROM_ROI_CUSTOMIZATION',
    (roiId: string) => ({ payload: { roiId } }));
export const removeScrollToViewFromModelCustomizationMetadata = createAction('REMOVE_SCROLL_TO_VIEW_FROM_MODEL_CUSTOMIZATION_METADATA',
    (metadataId: string) => ({ payload: { metadataId } }));
export const addAdvancedRoiCustomization = createAction('ADD_ADVANCED_ROI_CUSTOMIZATION',
    (outputId: string, newRoiId: string, duplicatedRoiId?: string | undefined) => ({ payload: { outputId, newRoiId, duplicatedRoiId } }));
export const addModelCustomizationMetadataItem = createAction('ADD_MODEL_CUSTOMIZATION_METADATA_ITEM',
    (outputId: string) => ({ payload: { outputId } }));
export const changeRoiCustomizationToMatchGlobalRoi = createAction('CHANGE_ROI_CUSTOMIZATION_TO_MATCH_GLOBAL_ROI',
    (roiId: string, globalRoiId: string) => ({ payload: { roiId, globalRoiId } }));
export const makeRoiCustomizationGlobalRoi = createAction('MAKE_ROI_CUSTOMIZATION_GLOBAL_ROI',
    (roiId: string, globalRoiId: string) => ({ payload: { roiId, globalRoiId } }));
export const removeAdvancedRoiCustomization = createAction('REMOVE_ADVANCED_ROI_CUSTOMIZATION',
    (roiId: string) => ({ payload: { roiId } }));
export const undoRoiCustomizationChanges = createAction('UNDO_ROI_CUSTOMIZATION_CHANGES',
    (roiId: string, isGlobalRoi: boolean) => ({ payload: { roiId, isGlobalRoi } }));
export const updateModelMetadataItem = createAction('UPDATE_MODEL_METADATA_ITEM',
    (metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean) => ({ payload: { metadataId, attribute, value, isUndoOperation } }));
export const updateModelCustomizationDescription = createAction('UPDATE_MODEL_CUSTOMIZATION_DESCRIPTION',
    (customizationBaseId: string, description: string) => ({ payload: { customizationBaseId, description } }));
export const removeModelMetadataItem = createAction('REMOVE_MODEL_METADATA_ITEM', (metadataId: string) => ({ payload: { metadataId } }));
export const undoAllCustomizationChanges = createAction('UNDO_ALL_CUSTOMIZATION_CHANGES');
export const duplicateCustomizationBase = createAction('DUPLICATE_CUSTOMIZATION_BASE',
    (customizationBaseId: string, newCustomizationName: string) => ({ payload: { customizationBaseId, newCustomizationName } }));
export const duplicateCustomizationOutput = createAction('DUPLICATE_CUSTOMIZATION_OUTPUT',
    (customizationOutputId: string, newFilename: string) => ({ payload: { customizationOutputId, newFilename } }));
export const renameCustomizationModel = createAction('RENAME_CUSTOMIZATION_MODEL',
    (customizationBaseId: string, name: string) => ({ payload: { customizationBaseId, name } }));
export const removeCustomizationBase = createAction('REMOVE_CUSTOMIZATION_BASE',
    (customizationBaseId: string) => ({ payload: { customizationBaseId } }));
export const removeCustomizationOutput = createAction('REMOVE_CUSTOMIZATION_OUTPUT',
    (customizationOutputId: string) => ({ payload: { customizationOutputId } }));

export const saveModelCustomization = createAction('SAVE_MODEL_CUSTOMIZATION',
    (appClient: MVisionAppClient | undefined) => ({ payload: { appClient } }));
export const saveModelCustomizationStart = createAction('SAVE_MODEL_CUSTOMIZATION_START');
export const saveModelCustomizationFinish = createAction('SAVE_MODEL_CUSTOMIZATION_FINISH',
    (saveWasSuccessful: boolean, errorMessage?: string, error?: Error) => ({ payload: { saveWasSuccessful, errorMessage, error } }));

export const importModelCustomization = createAction('IMPORT_MODEL_CUSTOMIZATION', (json: ModelCustomizationEntities | null) => ({ payload: { json } }));
export const importModelCustomizationFailure = createAction('IMPORT_MODEL_CUSTOMIZATION_FAILURE', (error: string | null) => ({ payload: { error } }));
export const exportModelCustomization = createAction('EXPORT_MODEL_CUSTOMIZATION', (appClient: MVisionAppClient | undefined) => ({ payload: { appClient } }));

export const resetAllModelCustomizations = createAction('RESET_ALL_MODEL_CUSTOMIZATIONS',
    (appClient: MVisionAppClient | undefined) => ({ payload: { appClient } }));
export const resetSingleModelCustomization = createAction('RESET_SINGLE_MODEL_CUSTOMIZATION', (customizationOutputId: string) => ({ payload: { customizationOutputId } }));
export const replaceModelCustomizationOutput = createAction('REPLACE_MODEL_CUSTOMIZATION_OUTPUT', (customizationOutputId: string, newOutputEntities: ModelCustomizationOutputEntities) => ({ payload: { customizationOutputId, newOutputEntities } }));

export const excludeDeprecatedModels = createAction('EXCLUDE_DEPRECATED_MODELS');
export const includeDeprecatedModels = createAction('INCLUDE_DEPRECATED_MODELS');
export const excludeNonLicensedModels = createAction('EXCLUDE_NON_LICENSED_MODELS');
export const includeNonLicensedModels = createAction('INCLUDE_NON_LICENSED_MODELS');
export const includeUserHiddenModels = createAction('INCLUDE_USER_HIDDEN_MODELS');
export const excludeUserHiddenModels = createAction('EXCLUDE_USER_HIDDEN_MODELS');
export const resetModelCustomizationStart = createAction('RESET_MODEL_CUSTOMIZATION_START');
export const resetModelCustomizationFinish = createAction('RESET_MODEL_CUSTOMIZATION_FINISH',
    (saveWasSuccessful: boolean, errorMessage?: string, error?: Error) => ({ payload: { saveWasSuccessful, errorMessage, error } }));
export const resetSingleCustomizationOutputStart = createAction('RESET_SINGLE_MODEL_CUSTOMIZATION_START');
export const resetSingleCustomizationOutputFinish = createAction('RESET_SINGLE_MODEL_CUSTOMIZATION_FINISH',
    (resetWasSuccessful: boolean, errorMessage?: string, error?: Error) => ({ payload: { resetWasSuccessful, errorMessage, error } }));

export const showFullRoiDetails = createAction('SHOW_FULL_ROI_DETAILS',
    (roiDetails: FullRoiDetailsId) => ({ payload: { roiDetails } }));
export const hideFullRoiDetails = createAction('HIDE_FULL_ROI_DETAILS');

export const fetchLicenseStatus = createAction('FETCH_LICENSE_STATUS', (appClient: MVisionAppClient | undefined) => ({ payload: { appClient } }));
export const setLicenseStatus = createAction('SET_LICENSE_STATUS',
    (licenseStatus: LicenseStatus | null, error?: string) => ({ payload: { licenseStatus, error } }));
export const updateLicenseStart = createAction('UPDATE_LICENSE_START',
    (newLicense: string, appClient: MVisionAppClient | undefined) => ({ payload: { newLicense, appClient } }));
export const updateLicenseFinish = createAction('UPDATE_LICENSE_FINISH',
    (success: boolean, error?: string) => ({ payload: { success, error } }));

export const fetchDaemonConfigs = createAction('FETCH_DAEMON_CONFIGS', (appClient: MVisionAppClient | undefined) => ({ payload: { appClient } }));
export const setDaemonConfigs = createAction('SET_DAEMON_CONFIGS',
    (daemonConfigs: DaemonConfig[] | null, error?: string) => ({ payload: { daemonConfigs, error } }));
export const setDaemonConfig = createAction('SET_DAEMON_CONFIG',
    (daemonConfig: DaemonConfig | null, error?: string) => ({ payload: { daemonConfig, error } }));
export const unsetDaemonConfig = createAction('UNSET_DAEMON_CONFIG',
    (daemonSessionId: string) => ({ payload: { daemonSessionId } }));
export const saveDaemonConfig = createAction('SAVE_DAEMON_CONFIG',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) => ({ payload: { appClient, daemonConfig } }));
export const saveDaemonConfigStart = createAction('SAVE_DAEMON_CONFIG_START',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) => ({ payload: { appClient, daemonConfig } }));
export const saveDaemonConfigFinish = createAction('SAVE_DAEMON_CONFIG_FINISH',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig, saveWasSuccessful: boolean, errorMessage?: string, error?: Error) => ({ payload: { appClient, daemonConfig, saveWasSuccessful, errorMessage, error } }));
export const resetDaemonConfig = createAction('RESET_DAEMON_CONFIG',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) => ({ payload: { appClient, daemonConfig } }));
export const resetDaemonConfigStart = createAction('RESET_DAEMON_CONFIG_START',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) => ({ payload: { appClient, daemonConfig } }));
export const resetDaemonConfigFinish = createAction('RESET_DAEMON_CONFIG_FINISH',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig, saveWasSuccessful: boolean, errorMessage?: string, error?: Error) => ({ payload: { appClient, daemonConfig, saveWasSuccessful, errorMessage, error } }));
export const removeDaemonConfig = createAction('REMOVE_DAEMON_CONFIG',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) => ({ payload: { appClient, daemonConfig } }));
export const removeDaemonConfigStart = createAction('REMOVE_DAEMON_CONFIG_START',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig) => ({ payload: { appClient, daemonConfig } }));
export const removeDaemonConfigFinish = createAction('REMOVE_DAEMON_CONFIG_FINISH',
    (appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig, saveWasSuccessful: boolean, errorMessage?: string, error?: Error) => ({ payload: { appClient, daemonConfig, saveWasSuccessful, errorMessage, error } }));

export const changeAeTitleRuleAction = createAction('CHANGE_AE_TITLE_RULE_ACTION', (id: string, action: string) => ({ payload: { id, action } }));
export const changeDicomAttributeRuleAction = createAction('CHANGE_DICOM_ATTRIBUTE_RULE_ACTION', (id: string, dicomAttribute: string, dicomValue: string) => ({ payload: { id, dicomAttribute, dicomValue } }));
export const addAeTitleRule = createAction('ADD_AE_TITLE_RULE', (aeTitleRule: AeTitleRule) => ({ payload: { aeTitleRule } }));
export const addDicomRule = createAction('ADD_DICOM_RULE', (dicomRule: DicomRule) => ({ payload: { dicomRule } }));
export const addDicomAttributeRule = createAction('ADD_DICOM_ATTRIBUTE_RULE', (dicomAttributeRule: DicomAttributeRule) => ({ payload: { dicomAttributeRule } }));
export const removeAeTitleRule = createAction('REMOVE_AE_TITLE_RULE', (id: string) => ({ payload: { id } }));
export const removeDicomRule = createAction('REMOVE_DICOM_RULE', (id: string) => ({ payload: { id } }));
export const removeDicomAttributeRule = createAction('REMOVE_DICOM_ATTRIBUTE_RULE', (id: string) => ({ payload: { id } }));
export const revertModelSelectionChanges = createAction('REVERT_MODEL_SELECTION_CHANGES', (revertScope: ModelSelectionScope, id?: string) => ({ payload: { revertScope, id } }));

export const fetchLabeling = createAction('FETCH_LABELING');
export const setLabeling = createAction('SET_LABELING',
    (productLabel: Labeling | null, error?: string) => ({ payload: { productLabel, error } }));

export const fetchClientList = createAction('FETCH_CLIENT_LIST');
export const fetchAccessRights = createAction('FETCH_ACCESS_RIGHTS', (deploymentMode?: DeploymentMode) => ({ payload: { deploymentMode } })); 
export const setClientList = createAction('SET_CLIENT_LIST', (clientList: MVisionAppClient[] | null, error?: string) => ({ payload: { clientList, error } }));
export const setCurrentAppClient = createAction('SET_CURRENT_APP_CLIENT', (userId: string | undefined) => ({ payload: { userId } }));
export const startFetchingClientData = createAction('START_FETCHING_CLIENT_DATA');
export const finishFetchingClientData = createAction('FINISH_FETCHING_CLIENT_DATA');

export const setAppConfig = createAction('SET_APP_CONFIG', (appConfig: ConfigInterface) => ({ payload: { appConfig } }));
export const startAppInitialization = createAction('START_APP_INITIALIZATION');
export const appInitializeSuccess = createAction('APP_INITIALIZE_SUCCESS');
export const appInitializeFailure = createAction('APP_INITIALIZE_FAILURE', (initializationErrorMessage: string) => ({ payload: { initializationErrorMessage } }));
export const setAppVersion = createAction('SET_APP_VERSION', (appVersion: AppVersionInfo) => ({ payload: { appVersion } }));
export const setAppDeploymentInfo = createAction('SET_APP_DEPLOYMENT_INFO', (appDeploymentInfo: DeploymentConfigInfo) => ({ payload: { appDeploymentInfo } }));
export const setHelpDocuments = createAction('SET_HELP_DOCUMENTS', (helpDocuments: HelpDocument[]) => ({ payload: { helpDocuments } }));
export const snoozeNewVersionAlertFor = createAction('SNOOZE_NEW_VERSION_ALERT_FOR', (snoozeForMs: number) => ({ payload: { snoozeForMs } }));
export const setNewVersionAlertSnooze = createAction('SET_NEW_VERSION_ALERT_SNOOZE', (isSnoozeEnabled: boolean) => ({ payload: { isSnoozeEnabled } }))

export const addAppAuthState = createAction('ADD_APP_AUTH_STATE', (appAuthState: AppAuthState) => ({ payload: { appAuthState } }));
export const initializeLogins = createAction('INIT_LOGINS', (appAuthState: AppAuthState | undefined) => ({ payload: { appAuthState } }));
export const startAppAuthLogin = createAction('START_APP_AUTH_LOGIN', (appAuthName: string) => ({ payload: { appAuthName } }));
export const setLoginError = createAction('SET_LOGIN_ERROR', (loginError: LoginError | null) => ({ payload: { loginError } }));
export const logInSuccess = createAction('LOG_IN_SUCCESS', (appAuthName: string) => ({ payload: { appAuthName } }));
export const requestLogOut = createAction('REQUEST_LOG_OUT');

export const fetchUserSettings = createAction('FETCH_USER_SETTINGS');
export const setUserSettings = createAction('SET_USER_SETTINGS', (userSettings: UserSettings | null, error?: string) => ({ payload: { userSettings, error } }));
export const setAccessRights = createAction('SET_ACCESS_RIGHTS', (accessRights: AccessRights | null, error?: string ) => ({ payload: { accessRights, error } }));
export const updateUserSettings = createAction('UPDATE_USER_SETTINGS', (settingsToUpdate: Partial<UserSettings>) => ({ payload: { settingsToUpdate } }));


export const removeModelCustomizationPageFocus = createAction('REMOVE_MODEL_CUSTOMIZATION_PAGE_FOCUS');

export const fetchStatistics = createAction('FETCH_STATISTICS', (appClient: MVisionAppClient | undefined) => ({ payload: { appClient } }));

// unit test actions
export const unitTestSetCustomizationObjectPathAsModified = createAction('UNIT_TEST_SET_CUSTOMIZATION_OBJECT_PATH_AS_MODIFIED',
    (objectIds: string[], objectType: CustomizationObjectType) => ({ payload: { objectIds, objectType } }));
export const unitTestAddFormValidationError = createAction('UNIT_TEST_ADD_FORM_VALIDATION_ERROR', (formError: FormValidationError) => ({ payload: { formError } }));
export const unitTestRemoveFormValidationError = createAction('UNIT_TEST_REMOVE_FORM_VALIDATION_ERROR', (formErrorId: string) => ({ payload: { formErrorId } }));

// ENTITY ADAPTERS

const modelAdapter = createEntityAdapter<SegmentationModel>();
const customizationBaseAdapter = createEntityAdapter<ModelCustomizationBase>();
const customizationOutputAdapter = createEntityAdapter<ModelCustomizationOutput>();
const modelCustomizationsMetadataAdapter = createEntityAdapter<ModelCustomizationMetadataItem>();
const roiCustomizationAdapter = createEntityAdapter<RoiCustomization>();
const globalRoiCustomizationAdapter = createEntityAdapter<GlobalRoiCustomization>();
const aeTitleRuleAdapter = createEntityAdapter<AeTitleRule>();
const dicomRuleAdapter = createEntityAdapter<DicomRule>();
const dicomAttributeRuleAdapter = createEntityAdapter<DicomAttributeRule>();
const customizationValidationErrorAdapter = createEntityAdapter<CustomizationValidationError>();
const formValidationErrorAdapter = createEntityAdapter<FormValidationError>();

const daemonValidationErrorAdapter = createEntityAdapter<DaemonValidationError>();

// entity adapter selectors -- these should not be used directly, but through selectors.tsx
export const modelAdapterSelectors = modelAdapter.getSelectors((state: StoreState) => state.segmentationModels);
export const customizationBaseAdapterSelectors = customizationBaseAdapter.getSelectors((state: StoreState) => state.customizationBases);
export const customizationOutputAdapterSelectors = customizationOutputAdapter.getSelectors((state: StoreState) => state.customizationOutputs);
export const modelCustomizationsMetadataAdapterSelectors = modelCustomizationsMetadataAdapter.getSelectors((state: StoreState) => state.modelCustomizationsMetadata);
export const roiCustomizationAdapterSelectors = roiCustomizationAdapter.getSelectors((state: StoreState) => state.roiCustomizations);
export const globalRoiCustomizationAdapterSelectors = globalRoiCustomizationAdapter.getSelectors((state: StoreState) => state.globalRoiCustomizations);
export const aeTitleRuleAdapterSelectors = aeTitleRuleAdapter.getSelectors((state: StoreState) => state.aeTitleRules);
export const dicomRuleAdapterSelectors = dicomRuleAdapter.getSelectors((state: StoreState) => state.dicomRules);
export const dicomAttributeRuleAdapterSelectors = dicomAttributeRuleAdapter.getSelectors((state: StoreState) => state.dicomAttributeRules);
export const customizationValidationErrorAdapterSelectors = customizationValidationErrorAdapter.getSelectors((state: StoreState) => state.customizationValidationErrors);
export const formValidationErrorAdapterSelectors = formValidationErrorAdapter.getSelectors((state: StoreState) => state.formValidationErrors);

// selectors for 'original' data
export const originalCustomizationsMetadataAdapterSelectors = modelCustomizationsMetadataAdapter.getSelectors((state: StoreState) => state.originalModelCustomizationsMetadata);
export const originalRoiCustomizationAdapterSelectors = roiCustomizationAdapter.getSelectors((state: StoreState) => state.originalRoiCustomizations);

export const daemonValidationErrorAdapterSelectors = daemonValidationErrorAdapter.getSelectors((state: StoreState) => state.daemonValidationErrors);

// TODO: use types here instead of complex classes
export type StoreState = {
    appConfig: ConfigInterface | undefined,
    isAppInitialized: boolean,
    isClientListFetched: boolean,
    appInitializationError: string | undefined,
    appVersion: AppVersionInfo | undefined;
    appDeploymentInfo: DeploymentConfigInfo | undefined;
    helpDocuments: HelpDocument[],
    isNewAppVersionAvailable: boolean,
    isNewVersionAlertSnoozing: boolean,
    isFetchingClientData: boolean,
    userSettings: UserSettings,
    accessRights: AccessRights,

    /** Current cloud backend, if set. */
    cloudBackend: CloudBackend | undefined,

    customizationFetchError: string | null,
    customizationImportExportError: string | null,
    textEditorFiles: TextEditorFile[],
    isLoadingTextEditorFilePaths: boolean,

    segmentationModels: EntityState<SegmentationModel>;
    customizationBases: EntityState<ModelCustomizationBase>;
    customizationOutputs: EntityState<ModelCustomizationOutput>;
    modelCustomizationsMetadata: EntityState<ModelCustomizationMetadataItem>;
    roiCustomizations: EntityState<RoiCustomization>;
    globalRoiCustomizations: EntityState<GlobalRoiCustomization>;
    aeTitleRules: EntityState<AeTitleRule>;
    dicomRules: EntityState<DicomRule>;
    dicomAttributeRules: EntityState<DicomAttributeRule>;
    customizationValidationErrors: EntityState<CustomizationValidationError>;
    formValidationErrors: EntityState<FormValidationError>;

    // these are for storing data directly from the backend, so any local modifications can be easily reverted back to them
    originalSegmentationModels: EntityState<SegmentationModel>;
    originalCustomizationBases: EntityState<ModelCustomizationBase>;
    originalCustomizationOutputs: EntityState<ModelCustomizationOutput>;
    originalModelCustomizationsMetadata: EntityState<ModelCustomizationMetadataItem>;
    originalRoiCustomizations: EntityState<RoiCustomization>;
    originalGlobalRoiCustomizations: EntityState<GlobalRoiCustomization>;
    originalAeTitleRules: EntityState<AeTitleRule>;
    originalDicomRules: EntityState<DicomRule>;
    originalDicomAttributeRules: EntityState<DicomAttributeRule>;

    /** True if we're currently saving model selection config, false otherwise. */
    isModelCustomizationSavingInProgress: boolean,

    /** True if we're currently deleting model selection config, false otherwise. */
    isModelCustomizationDeleteInProgress: boolean,

    /** True if we're currently resetting a single model customization, false otherwise. */
    isCustomizationOutputResetInProgress: boolean,

    /** True if deprecated models are shown, false otherwise. */
    areDeprecatedModelsShown: boolean,

    /** True if models not within current license are shown, false otherwise. */
    areNonLicensedModelsShown: boolean,

    /** True if models hidden by current user are shown, false otherwise. */
    areUserHiddenModelsShown: boolean,

    fullRoiDetails: FullRoiDetailsId | undefined,

    /** Model selection save error, if any. */
    modelCustomizationSaveError: string | null,

    /** An error regarding model customization data that's not directly related to saving */
    modelCustomizationDataError: string | null,

    /** Daemon configs for current user/app. Zero, one, or multiple configs. */
    daemonConfigs: DaemonConfig[] | null,

    /** Error when fetching daemon configs, if any. */
    daemonConfigsError: string | null,

    /** True if we're currently saving a daemon config, false otherwise. */
    isDaemonConfigSaveInProgress: boolean,

    /** True if we're currently resetting daemon config, false otherwise. */
    isDaemonConfigResetInProgress: boolean,

    /** True if we're currently deleting daemon config, false otherwise. */
    isDaemonConfigDeleteInProgress: boolean,

    /** Daemon config save error, if any. */
    daemonConfigSaveError: string | null,
    daemonValidationErrors: EntityState<DaemonValidationError>;

    /** License status for current user/app. One or multiple licenses. */
    licenseStatus: LicenseStatus[] | null,

    /** Error when fetching licenses, if any. */
    licenseStatusError: string | null,

    isUpdatingLicense: boolean,
    licenseUpdateError: string | null,
    successfulLicenseUpdatesCount: number,
    productLabel: Labeling | null,
    productLabelError: string | null,

    /** Keep track of which authentications are required for the app and their user flow. */
    appAuthStates: AppAuthState[],

    /** Latest login error, if any. */
    loginError: LoginError | null,

    /** Latest access Rights error, if any */
    accessRightsError: string | null,

    /** List of MVision App clients available to current user. */
    clientList: MVisionAppClient[] | undefined,

    /** Error received when trying to fetch client list, or null. TODO: Consider a more simplified error messaging state handling. */
    clientListError: string | null,

    /** Error received when trying to fetch user settings, or null. TODO: Consider a more simplified error messaging state handling. */
    userSettingsError: string | null,

    /** Current user-selected app client, or nothing. */
    currentAppClient: MVisionAppClient | undefined;

    /** Change model customization page to immediately show this item. */
    modelCustomizationPageFocusId: string | undefined;
}

export const initialState: StoreState = {
    appConfig: undefined,
    isAppInitialized: false,
    isClientListFetched: false,
    appInitializationError: undefined,
    appVersion: undefined,
    appDeploymentInfo: undefined,
    helpDocuments: [],
    isNewAppVersionAvailable: false,
    isNewVersionAlertSnoozing: false,
    isFetchingClientData: false,
    userSettings: getInitialUserSettings(),
    accessRights: getInitialUserAccessRights(),

    cloudBackend: undefined,

    customizationFetchError: null,
    customizationImportExportError: null,
    textEditorFiles: [],
    isLoadingTextEditorFilePaths: false,

    segmentationModels: modelAdapter.getInitialState(),
    customizationBases: customizationBaseAdapter.getInitialState(),
    customizationOutputs: customizationOutputAdapter.getInitialState(),
    modelCustomizationsMetadata: modelCustomizationsMetadataAdapter.getInitialState(),
    roiCustomizations: roiCustomizationAdapter.getInitialState(),
    globalRoiCustomizations: globalRoiCustomizationAdapter.getInitialState(),
    aeTitleRules: aeTitleRuleAdapter.getInitialState(),
    dicomRules: dicomRuleAdapter.getInitialState(),
    dicomAttributeRules: dicomAttributeRuleAdapter.getInitialState(),
    customizationValidationErrors: customizationValidationErrorAdapter.getInitialState(),
    formValidationErrors: formValidationErrorAdapter.getInitialState(),

    originalSegmentationModels: modelAdapter.getInitialState(),
    originalCustomizationBases: customizationBaseAdapter.getInitialState(),
    originalCustomizationOutputs: customizationOutputAdapter.getInitialState(),
    originalModelCustomizationsMetadata: modelCustomizationsMetadataAdapter.getInitialState(),
    originalRoiCustomizations: roiCustomizationAdapter.getInitialState(),
    originalGlobalRoiCustomizations: globalRoiCustomizationAdapter.getInitialState(),
    originalAeTitleRules: aeTitleRuleAdapter.getInitialState(),
    originalDicomRules: dicomRuleAdapter.getInitialState(),
    originalDicomAttributeRules: dicomAttributeRuleAdapter.getInitialState(),

    isModelCustomizationSavingInProgress: false,
    isModelCustomizationDeleteInProgress: false,
    isCustomizationOutputResetInProgress: false,
    areDeprecatedModelsShown: false,
    areNonLicensedModelsShown: false,
    areUserHiddenModelsShown: false,
    modelCustomizationSaveError: null,
    modelCustomizationDataError: null,
    fullRoiDetails: undefined,

    daemonConfigs: null,
    daemonConfigsError: null,
    isDaemonConfigSaveInProgress: false,
    isDaemonConfigResetInProgress: false,
    isDaemonConfigDeleteInProgress: false,
    daemonConfigSaveError: null,
    daemonValidationErrors: daemonValidationErrorAdapter.getInitialState(),

    licenseStatus: null,
    licenseStatusError: null,
    isUpdatingLicense: false,
    licenseUpdateError: null,
    successfulLicenseUpdatesCount: 0,
    productLabel: null,
    productLabelError: null,
    appAuthStates: [],
    loginError: null,
    accessRightsError: null,
    clientList: undefined,
    clientListError: null,
    userSettingsError: null,
    currentAppClient: undefined,

    modelCustomizationPageFocusId: undefined,
}


// internal helper types and enums

// note: GlobalRoiCustomization is intentionally excluded as it's not really a part of this hierarchy
type AnyCustomizationType = SegmentationModel | ModelCustomizationBase | ModelCustomizationOutput | ModelCustomizationMetadataItem | RoiCustomization |
    AeTitleRule | DicomRule | DicomAttributeRule;



// createReducer from redux toolkit uses immer internally
// NOTE: any CLASS objects stored in the redux store must extend from immerable.tsx to work properly -- but classes should be avoided. Pure javascript objects (e.g. types)
// work better!
export const reducer = createReducer(initialState, {

    [appInitializeSuccess.toString()]: (state: StoreState) => {
        state.isAppInitialized = true;
        state.appInitializationError = undefined;
    },
    [appInitializeFailure.toString()]: (state: StoreState, action: { payload: { initializationErrorMessage: string } }) => {
        const { initializationErrorMessage } = action.payload;
        state.isAppInitialized = false;
        state.appInitializationError === undefined ? state.appInitializationError = initializationErrorMessage : state.appInitializationError += `\n${initializationErrorMessage}`;
    },
    [setAppVersion.toString()]: (state: StoreState, action: { payload: { appVersion: AppVersionInfo } }) => {
        const { appVersion } = action.payload;

        // set app version if it's not yet been defined -- otherwise see if it's changed & inform the user to
        // refresh their page if needed
        if (state.appVersion === undefined) {
            state.appVersion = appVersion;

            // set correct version to app auths
            Object.values(backendTierAppAuths).forEach(appAuth => appAuth.setAppVersion(appVersion));
        } else {
            if (!areVersionsSame(state.appVersion, appVersion)) {
                state.isNewAppVersionAvailable = true;
            }
        }
    },
    [setAppDeploymentInfo.toString()]: (state: StoreState, action: { payload: { appDeploymentInfo: DeploymentConfigInfo } }) => {
        const { appDeploymentInfo } = action.payload;
        state.appDeploymentInfo = appDeploymentInfo;

        // set cloud backend if relevant
        if (appDeploymentInfo.deploymentMode === DeploymentMode.Cloud && appDeploymentInfo.cloudBackendUrl !== undefined && appDeploymentInfo.cloudBackendTier !== undefined) {
            const backend = createBackend(appDeploymentInfo.backendName || 'Backend', appDeploymentInfo.cloudBackendUrl, appDeploymentInfo.cloudBackendTier);
            state.cloudBackend = backend;
        }
    },
    [setHelpDocuments.toString()]: (state: StoreState, action: { payload: { helpDocuments: HelpDocument[] } }) => {
        const { helpDocuments } = action.payload;
        state.helpDocuments = helpDocuments;
    },
    [setNewVersionAlertSnooze.toString()]: (state: StoreState, action: { payload: { isSnoozeEnabled: boolean } }) => {
        const { isSnoozeEnabled } = action.payload;
        state.isNewVersionAlertSnoozing = isSnoozeEnabled;
    },
    [setAppConfig.toString()]: (state: StoreState, action: { payload: { appConfig: ConfigInterface } }) => {
        const { appConfig } = action.payload;
        state.appConfig = appConfig;
    },

    [setModelCustomizationObjectsAsModified.toString()]: (state: StoreState, action: { payload: { updates: Partial<ModelCustomizationEntities> | null, error?: string } }) => {
        if (action.payload.updates !== null) {
            const aeTitleRuleIds = action.payload.updates.aeTitleRules?.map(id => id.id);
            const dicomRuleIds = action.payload.updates.dicomRules?.map(id => id.id);
            const dicomAttributeRuleIds = action.payload.updates.dicomAttributeRules?.map(id => id.id);
            const roiIds = action.payload.updates.roiCustomizations?.map(id => id.id);
            const globalRoiIds = action.payload.updates.globalRoiCustomizations?.map(id => id.id);
            const modelsIds = action.payload.updates.models?.map(id => id.id);
            const customizationBasesIds = action.payload.updates.customizationBases?.map(id => id.id);
            const customizationOutputsIds = action.payload.updates.customizationOutputs?.map(id => id.id);

            if (aeTitleRuleIds) {
                aeTitleRuleAdapter.updateMany(
                    state.aeTitleRules,
                    aeTitleRuleIds.map(aeTitleRuleId => ({ id: aeTitleRuleId, changes: { isModified: true } }))
                );
            }
            if (dicomRuleIds) {
                dicomRuleAdapter.updateMany(
                    state.dicomRules,
                    dicomRuleIds.map(dicomRuleId => ({ id: dicomRuleId, changes: { isModified: true } }))
                );
            }
            if (dicomAttributeRuleIds) {
                dicomAttributeRuleAdapter.updateMany(
                    state.dicomAttributeRules,
                    dicomAttributeRuleIds.map(dicomAttributeRuleId => ({ id: dicomAttributeRuleId, changes: { isModified: true } }))
                );
            }
            if (modelsIds) {
                modelAdapter.updateMany(
                    state.segmentationModels,
                    modelsIds.map(modelId => ({ id: modelId, changes: { isModified: true } }))
                );
            }
            if (roiIds) {
                roiCustomizationAdapter.updateMany(
                    state.roiCustomizations,
                    roiIds.map(roiId => ({ id: roiId, changes: { isModified: true } }))
                );
            }
            if (globalRoiIds) {
                globalRoiCustomizationAdapter.updateMany(
                    state.globalRoiCustomizations,
                    globalRoiIds.map(globalRoiId => ({ id: globalRoiId, changes: { isModified: true } }))
                );
            }
            if (customizationBasesIds) {
                customizationBaseAdapter.updateMany(
                    state.customizationBases,
                    customizationBasesIds.map(customizationBaseId => ({ id: customizationBaseId, changes: { isModified: true } }))
                );
            }
            if (customizationOutputsIds) {
                customizationOutputAdapter.updateMany(
                    state.customizationOutputs,
                    customizationOutputsIds.map(customizationOutputId => ({ id: customizationOutputId, changes: { isModified: true } }))
                );
            }
        };
    },
    /******************************** */
    /**                               */
    /**                               */
    /** MODEL CUSTOMIZATION REDUCERS  */
    /**                               */
    /**                               */
    /******************************** */

    [setModelCustomizations.toString()]: (state: StoreState, action: { payload: { customizations: ModelCustomizationEntities | null, error?: string } }) => {
        const { customizations, error } = action.payload;

        if (customizations !== null) {
            // set everything according to input
            modelAdapter.setAll(state.segmentationModels, customizations.models);
            customizationBaseAdapter.setAll(state.customizationBases, customizations.customizationBases);
            customizationOutputAdapter.setAll(state.customizationOutputs, customizations.customizationOutputs);
            modelCustomizationsMetadataAdapter.setAll(state.modelCustomizationsMetadata, customizations.modelCustomizationsMetadata);
            roiCustomizationAdapter.setAll(state.roiCustomizations, customizations.roiCustomizations);
            globalRoiCustomizationAdapter.setAll(state.globalRoiCustomizations, customizations.globalRoiCustomizations);
            aeTitleRuleAdapter.setAll(state.aeTitleRules, customizations.aeTitleRules);
            dicomRuleAdapter.setAll(state.dicomRules, customizations.dicomRules);
            dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, customizations.dicomAttributeRules);

            // set original collections, also, so we can revert changes to the actual collection with ease
            modelAdapter.setAll(state.originalSegmentationModels, customizations.models);
            customizationBaseAdapter.setAll(state.originalCustomizationBases, customizations.customizationBases);
            customizationOutputAdapter.setAll(state.originalCustomizationOutputs, customizations.customizationOutputs);
            modelCustomizationsMetadataAdapter.setAll(state.originalModelCustomizationsMetadata, customizations.modelCustomizationsMetadata);
            roiCustomizationAdapter.setAll(state.originalRoiCustomizations, customizations.roiCustomizations);
            globalRoiCustomizationAdapter.setAll(state.originalGlobalRoiCustomizations, customizations.globalRoiCustomizations);
            aeTitleRuleAdapter.setAll(state.originalAeTitleRules, customizations.aeTitleRules);
            dicomRuleAdapter.setAll(state.originalDicomRules, customizations.dicomRules);
            dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, customizations.dicomAttributeRules);
        } else {
            // set everything to default values
            modelAdapter.setAll(state.segmentationModels, modelAdapter.getInitialState().entities as Record<string, SegmentationModel>);
            customizationBaseAdapter.setAll(state.customizationBases, customizationBaseAdapter.getInitialState().entities as Record<string, ModelCustomizationBase>);
            customizationOutputAdapter.setAll(state.customizationOutputs, customizationOutputAdapter.getInitialState().entities as Record<string, ModelCustomizationOutput>);
            modelCustomizationsMetadataAdapter.setAll(state.modelCustomizationsMetadata, modelCustomizationsMetadataAdapter.getInitialState().entities as Record<string, ModelCustomizationMetadataItem>);
            roiCustomizationAdapter.setAll(state.roiCustomizations, roiCustomizationAdapter.getInitialState().entities as Record<string, RoiCustomization>);
            globalRoiCustomizationAdapter.setAll(state.globalRoiCustomizations, globalRoiCustomizationAdapter.getInitialState().entities as Record<string, GlobalRoiCustomization>);
            aeTitleRuleAdapter.setAll(state.aeTitleRules, aeTitleRuleAdapter.getInitialState().entities as Record<string, AeTitleRule>);
            dicomRuleAdapter.setAll(state.dicomRules, dicomRuleAdapter.getInitialState().entities as Record<string, DicomRule>);
            dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, dicomAttributeRuleAdapter.getInitialState().entities as Record<string, DicomAttributeRule>);

            // set original collections, also, so we can revert changes to the actual collection with ease
            modelAdapter.setAll(state.originalSegmentationModels, modelAdapter.getInitialState().entities as Record<string, SegmentationModel>);
            customizationBaseAdapter.setAll(state.originalCustomizationBases, customizationBaseAdapter.getInitialState().entities as Record<string, ModelCustomizationBase>);
            customizationOutputAdapter.setAll(state.originalCustomizationOutputs, customizationOutputAdapter.getInitialState().entities as Record<string, ModelCustomizationOutput>);
            modelCustomizationsMetadataAdapter.setAll(state.originalModelCustomizationsMetadata, modelCustomizationsMetadataAdapter.getInitialState().entities as Record<string, ModelCustomizationMetadataItem>);
            roiCustomizationAdapter.setAll(state.originalRoiCustomizations, roiCustomizationAdapter.getInitialState().entities as Record<string, RoiCustomization>);
            globalRoiCustomizationAdapter.setAll(state.originalGlobalRoiCustomizations, globalRoiCustomizationAdapter.getInitialState().entities as Record<string, GlobalRoiCustomization>);
            aeTitleRuleAdapter.setAll(state.originalAeTitleRules, aeTitleRuleAdapter.getInitialState().entities as Record<string, AeTitleRule>);
            dicomRuleAdapter.setAll(state.originalDicomRules, dicomRuleAdapter.getInitialState().entities as Record<string, DicomRule>);
            dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, dicomAttributeRuleAdapter.getInitialState().entities as Record<string, DicomAttributeRule>);
        }

        state.customizationFetchError = error !== undefined ? error : null;
        if (error === undefined) {
            // clear any errors
            customizationValidationErrorAdapter.removeAll(state.customizationValidationErrors);
            formValidationErrorAdapter.removeAll(state.formValidationErrors);

            // calculate initial form validation errors
            for (const outputId of state.customizationOutputs.ids) {
                performFormValidationForOutput(state, outputId as string);
            }
        }
    },
    [updateModelVisibility.toString()]: (state: StoreState, action: { payload: { modelId: string, visibility: ModelVisibility } }) => {
        const { modelId, visibility } = action.payload;
        modelAdapter.updateOne(state.segmentationModels, { id: modelId, changes: { visibility: visibility, isModified: true } });
    },
    [importModelCustomizationFailure.toString()]: (state: StoreState, action: { payload: { error: string | null } }) => {
        const { error } = action.payload;
        state.customizationImportExportError = error;
    },
    [updateRoiCustomizationColor.toString()]: (state: StoreState, action: { payload: { roiId: string, color: [number, number, number], isGlobalRoi: boolean } }) => {
        const { roiId, color, isGlobalRoi } = action.payload;
        updateRoiCustomizationField(state, roiId, isGlobalRoi, { color: color, isModified: true });
    },
    [updateRoiCustomizationIncludedInModel.toString()]: (state: StoreState, action: { payload: { roiId: string, isIncluded: boolean, isGlobalRoi: boolean } }) => {
        const { roiId, isIncluded, isGlobalRoi } = action.payload;

        // isIncluded field doesn't affect global roi groupings so recalculation is unnecessary
        const noGlobalRoiRecalculate = true;

        updateRoiCustomizationField(state, roiId, isGlobalRoi, { isIncluded: isIncluded, isModified: true }, noGlobalRoiRecalculate);

        // perform form validation afterwards
        // if we have a global roi then start by collecting applicable roi ids
        const roiIdsForValidation = !isGlobalRoi ? [roiId] : state.globalRoiCustomizations.entities[roiId]?.coveredRois;
        if (roiIdsForValidation) {
            for (const roiIdForValidation of roiIdsForValidation) {
                // validate form changes after roi name was changed
                const roi = state.roiCustomizations.entities[roiIdForValidation];
                if (roi && roi.modelCustomizationOutputId) {
                    performFormValidationForOutput(state, roi.modelCustomizationOutputId);
                }
            }
        }
    },
    [toggleAllRoisInCustomizationOutput.toString()]: (state: StoreState, action: { payload: { customizationOutputId: string, isIncluded: boolean } }) => {
        const { customizationOutputId, isIncluded } = action.payload;
        const allRois = state.customizationOutputs.entities[customizationOutputId]?.rois;

        // isIncluded field doesn't affect global roi groupings so recalculation is unnecessary
        const noGlobalRoiRecalculate = true;

        // if isIncluded true, then update all rois to be included using updateRoiCustomizationField function
        if (allRois) {
            for (const roiId of allRois) {
                updateRoiCustomizationField(state, roiId as string, false, { isIncluded: isIncluded, isModified: true }, noGlobalRoiRecalculate);
            }
        }

        // perform form validation afterwards
        if (allRois) {
            for (const roiIdForValidation of allRois) {
                // validate form changes after roi name was changed
                const roi = state.roiCustomizations.entities[roiIdForValidation];
                if (roi && roi.modelCustomizationOutputId) {
                    performFormValidationForOutput(state, roi.modelCustomizationOutputId);
                }
            }
        }
    },
    [updateRoiCustomizationName.toString()]: (state: StoreState, action: { payload: { roiId: string, name: string, isGlobalRoi: boolean } }) => {
        const { roiId, name, isGlobalRoi } = action.payload;
        updateRoiCustomizationField(state, roiId, isGlobalRoi, { name: name, isModified: true });

        // perform form validation afterwards
        // if we have a global roi then start by collecting applicable roi ids
        const roiIdsForValidation = !isGlobalRoi ? [roiId] : state.globalRoiCustomizations.entities[roiId]?.coveredRois;
        if (roiIdsForValidation) {
            for (const roiIdForValidation of roiIdsForValidation) {
                // validate form changes after roi name was changed
                const roi = state.roiCustomizations.entities[roiIdForValidation];
                if (roi && roi.modelCustomizationOutputId) {
                    performFormValidationForOutput(state, roi.modelCustomizationOutputId);
                }
            }
        }
    },
    [updateRoiCustomizationOperation.toString()]: (state: StoreState, action: { payload: { roiId: string, operation: string, isGlobalRoi: boolean } }) => {
        const { roiId, operation, isGlobalRoi } = action.payload;
        const roiBeforeChanges = state.roiCustomizations.entities[roiId];
        updateRoiCustomizationField(state, roiId, isGlobalRoi, { operation: operation, isModified: true });

        // need to check that this roi isn't in an another global roi's exclusions
        // TODO: this would be an easier check to make if RoiCustomization.globalRoiId would also include global roi id if the roi is in that item's
        // exclusions, which currently doesn't happen. Consider this change if the following code has bad performance (and put it into calculateGlobalRoiChangesForRegularRoi())
        if (roiBeforeChanges) {
            const previousExcludedGlobalRoi = Object.values(state.globalRoiCustomizations.entities).find(g => g?.operation === roiBeforeChanges.operation && g?.excludedRois.includes(roiId));
            if (previousExcludedGlobalRoi) {
                const excludedRois = previousExcludedGlobalRoi.excludedRois.filter(rId => rId !== roiId);
                if (excludedRois.length === 0 && previousExcludedGlobalRoi.coveredRois.length === 0) {
                    globalRoiCustomizationAdapter.removeOne(state.globalRoiCustomizations, previousExcludedGlobalRoi.id);
                } else {
                    globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations,
                        {
                            id: previousExcludedGlobalRoi.id,
                            changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, previousExcludedGlobalRoi, previousExcludedGlobalRoi.coveredRois, previousExcludedGlobalRoi.excludedRois.filter(rId => rId !== roiId)) }
                        });
                }
            }
        }

    },
    [updateRoiInterpretedType.toString()]: (state: StoreState, action: { payload: { roiId: string, interpretedType: string, isGlobalRoi: boolean } }) => {
        const { roiId, interpretedType, isGlobalRoi } = action.payload;
        updateRoiCustomizationField(state, roiId, isGlobalRoi, { interpretedType: interpretedType, isModified: true });
    },
    [updateRoiFmaId.toString()]: (state: StoreState, action: { payload: { roiId: string, fmaId: Partial<FmaId>, isGlobalRoi: boolean } }) => {
        const { roiId, fmaId, isGlobalRoi } = action.payload;
        updateRoiCustomizationField(state, roiId, isGlobalRoi, { ...fmaId, isModified: true });
    },
    [copyRoiFmaIds.toString()]: (state: StoreState, action: { payload: { fmaIdAttributes: Partial<FmaId> } }) => {
        const { fmaIdAttributes } = action.payload;
        setFmaIdsForAllRois(state, fmaIdAttributes);
    },

    [copyRoiFmaIdsOutput.toString()]: (state: StoreState, action: { payload: { fmaIdAttributes: Partial<FmaId> } }) => {
        const { fmaIdAttributes } = action.payload;
        setFmaIdsForRoisInSameCustomizationOutput(state, fmaIdAttributes);
    },

    [updateRoiPhysicalProperty.toString()]: (state: StoreState, action: { payload: { roiId: string, physicalPropertyAttribute: PhysicalProperty | undefined, physicalPropertyValue: string | undefined, isGlobalRoi: boolean } }) => {
        const { roiId, physicalPropertyAttribute, physicalPropertyValue, isGlobalRoi } = action.payload;

        const physicalPropertyChanges: Partial<RoiCustomization> | Partial<GlobalRoiCustomization> = {};
        if (physicalPropertyAttribute !== undefined) { physicalPropertyChanges.physicalPropertyAttribute = physicalPropertyAttribute; }
        if (physicalPropertyValue !== undefined) { physicalPropertyChanges.physicalPropertyValue = physicalPropertyValue; }

        updateRoiCustomizationField(state, roiId, isGlobalRoi, { ...physicalPropertyChanges, isModified: true });
    },
    [updateModelCustomizationDescription.toString()]: (state: StoreState, action: { payload: { customizationBaseId: string, description: string } }) => {
        const { customizationBaseId, description } = action.payload;
        customizationBaseAdapter.updateOne(state.customizationBases, {
            id: customizationBaseId,
            changes: { description, isModified: true },
        });
        setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);
    },
    [addAdvancedRoiCustomization.toString()]: (state: StoreState, action: { payload: { outputId: string, newRoiId: string, duplicatedRoiId?: string | undefined } }) => {
        const { outputId, newRoiId, duplicatedRoiId } = action.payload;
        const output = state.customizationOutputs.entities[outputId];
        if (output === undefined) { throw new Error(`Could not find customization output ${outputId}`); }

        const duplicatedRoi = duplicatedRoiId ? state.roiCustomizations.entities[duplicatedRoiId] : undefined;

        const operation = duplicatedRoi ? duplicatedRoi.operation : '';
        const name = duplicatedRoi ? duplicatedRoi.name : '';
        const color = duplicatedRoi ? duplicatedRoi.color : generateRandomColor();
        const interpretedType = duplicatedRoi ? duplicatedRoi.interpretedType : DEFAULT_INTERPRETED_TYPE;
        const physicalPropertyAttribute = duplicatedRoi ? duplicatedRoi.physicalPropertyAttribute : PhysicalProperty.NotSet;
        const physicalPropertyValue = duplicatedRoi ? duplicatedRoi.physicalPropertyValue : '';
        const fmaId = duplicatedRoi ? getFmaId(duplicatedRoi) : getDefaultFmaId();
        const isIncluded = duplicatedRoi ? duplicatedRoi.isIncluded : true;
        const canBeDeleted = true;
        const isModified = true;
        const scrollToView = true;

        const newRoi = createNewRoiCustomization(operation, name, color, interpretedType, physicalPropertyAttribute, physicalPropertyValue, fmaId, isIncluded, canBeDeleted, outputId, newRoiId, isModified, scrollToView);
        roiCustomizationAdapter.addOne(state.roiCustomizations, newRoi);
        customizationOutputAdapter.updateOne(state.customizationOutputs, { id: outputId, changes: { rois: output.rois.concat(newRoiId), isModified: true } });
        setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);

        // update global roi if we were duplicating
        if (duplicatedRoi !== undefined) {
            if (duplicatedRoi.globalRoiId !== undefined) {
                const globalRoi = state.globalRoiCustomizations.entities[duplicatedRoi.globalRoiId];
                if (globalRoi !== undefined) {
                    globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                        id: duplicatedRoi.globalRoiId,
                        changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, globalRoi.coveredRois.concat(newRoiId), globalRoi.excludedRois) }
                    });
                }
            } else {
                const excludedGlobalRoi = Object.values(state.globalRoiCustomizations.entities).find(g => g?.operation === newRoi.operation);
                if (excludedGlobalRoi !== undefined) {
                    globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                        id: excludedGlobalRoi.id,
                        changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, excludedGlobalRoi, excludedGlobalRoi.coveredRois, excludedGlobalRoi.excludedRois.concat(newRoiId)) }
                    });
                }
            }
        }

        // validate form changes after a new advanced roi (with most likely a duplicated name) was added
        performFormValidationForOutput(state, outputId);
    },
    [addModelCustomizationMetadataItem.toString()]: (state: StoreState, action: { payload: { outputId: string } }) => {
        const { outputId } = action.payload;
        const output = state.customizationOutputs.entities[outputId];
        if (output === undefined) { throw new Error(`Could not find customization output ${outputId}`); }

        const newMetadata = createNewMetadataItem('', '', outputId);
        newMetadata.isModified = true;
        newMetadata.scrollToView = true;
        modelCustomizationsMetadataAdapter.addOne(state.modelCustomizationsMetadata, newMetadata);
        customizationOutputAdapter.updateOne(state.customizationOutputs, { id: outputId, changes: { metadata: output.metadata.concat(newMetadata.id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);
    },
    [removeScrollToViewFromRoiCustomization.toString()]: (state: StoreState, action: { payload: { roiId: string } }) => {
        const { roiId } = action.payload;
        roiCustomizationAdapter.updateOne(state.roiCustomizations, { id: roiId, changes: { scrollToView: false } });
    },
    [removeScrollToViewFromModelCustomizationMetadata.toString()]: (state: StoreState, action: { payload: { metadataId: string } }) => {
        const { metadataId } = action.payload;
        modelCustomizationsMetadataAdapter.updateOne(state.modelCustomizationsMetadata, { id: metadataId, changes: { scrollToView: false } });
    },
    [changeRoiCustomizationToMatchGlobalRoi.toString()]: (state: StoreState, action: { payload: { roiId: string, globalRoiId: string } }) => {
        const { roiId, globalRoiId } = action.payload;
        const globalRoi = state.globalRoiCustomizations.entities[globalRoiId];
        if (globalRoi) {
            const coveredRois = globalRoi.coveredRois.includes(roiId) ? globalRoi.coveredRois : globalRoi.coveredRois.concat(roiId);
            const excludedRois = globalRoi.excludedRois.filter(rId => rId !== roiId);
            globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, { id: globalRoiId, changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, coveredRois, excludedRois) } });
            roiCustomizationAdapter.updateOne(state.roiCustomizations, {
                id: roiId, changes: {
                    name: globalRoi.name,
                    operation: globalRoi.operation,
                    color: globalRoi.color,
                    globalRoiId: globalRoiId,
                    interpretedType: globalRoi.interpretedType,
                    ...getFmaId(globalRoi),
                    isModified: true,
                }
            });
            roiCustomizationAdapter.updateOne(state.roiCustomizations, { id: roiId, changes: { scrollToView: false } });
            setObjectAndAncestorsAsModified(state, [roiId], CustomizationObjectType.Roi);

            // validate form changes within output after a roi was changed to match a global roi
            const roi = state.roiCustomizations.entities[roiId];
            if (roi && roi.modelCustomizationOutputId) {
                performFormValidationForOutput(state, roi.modelCustomizationOutputId);
            }
        }
    },
    [makeRoiCustomizationGlobalRoi.toString()]: (state: StoreState, action: { payload: { roiId: string, globalRoiId: string } }) => {
        const { roiId, globalRoiId } = action.payload;
        const roi = state.roiCustomizations.entities[roiId];
        const globalRoi = state.globalRoiCustomizations.entities[globalRoiId];
        if (roi && globalRoi) {
            const globalRoiChanges: Partial<GlobalRoiCustomization> = {
                name: roi.name, operation: roi.operation, color: roi.color,
                interpretedType: roi.interpretedType,
                ...getFmaId(roi),
            };

            const allRoiIds = globalRoi.coveredRois.concat(globalRoi.excludedRois);
            if (!allRoiIds.includes(roiId)) { allRoiIds.push(roiId); }

            const coveredRois: string[] = [];
            const excludedRois: string[] = [];
            const removedRois: string[] = [];
            let isBuiltInRoi = false;
            for (const rId of allRoiIds) {
                const r = state.roiCustomizations.entities[rId];
                if (r) {
                    if (roiCustomizationMatchesGlobalRoi(r, globalRoi, globalRoiChanges)) {
                        coveredRois.push(rId);
                        isBuiltInRoi = isBuiltInRoi || r.isBuiltInRoi;
                    } else if (r.operation === globalRoiChanges.operation) {
                        excludedRois.push(rId);
                    } else {
                        removedRois.push(rId);
                    }
                }
            }

            globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                id: globalRoiId, changes:
                {
                    ...globalRoiChanges,
                    ...getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, coveredRois, excludedRois),
                    isBuiltInRoi,
                }
            });
            roiCustomizationAdapter.updateMany(state.roiCustomizations, coveredRois.map(c => ({ id: c, changes: { globalRoiId: globalRoiId } })));
            roiCustomizationAdapter.updateMany(state.roiCustomizations, excludedRois.concat(removedRois).map(c => ({ id: c, changes: { globalRoiId: undefined } })));
        }
    },
    [removeAdvancedRoiCustomization.toString()]: (state: StoreState, action: { payload: { roiId: string } }) => {
        const { roiId } = action.payload;
        const roi = state.roiCustomizations.entities[roiId];
        if (roi && !roi.isBuiltInRoi) {
            const globalRoiId = roi.globalRoiId;
            const outputId = roi.modelCustomizationOutputId;

            const globalRoi = globalRoiId ? state.globalRoiCustomizations.entities[globalRoiId] : Object.values(state.globalRoiCustomizations.entities).find(g => g?.excludedRois.includes(roiId));
            if (globalRoi) {
                globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                    id: globalRoi.id,
                    changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, globalRoi.coveredRois.filter(rId => rId !== roiId), globalRoi.excludedRois.filter(rId => rId !== roiId)) }
                });
            }

            const output = outputId ? state.customizationOutputs.entities[outputId] : undefined;
            if (outputId && output) {
                customizationOutputAdapter.updateOne(state.customizationOutputs, { id: outputId, changes: { rois: output.rois.filter(rId => rId !== roiId), isModified: true } });
                setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);
            }

            roiCustomizationAdapter.removeOne(state.roiCustomizations, roiId);
            customizationValidationErrorAdapter.removeOne(state.customizationValidationErrors, roiId);
            formValidationErrorAdapter.removeMany(state.formValidationErrors, getFormValidationErrorIdsForItem(state, roiId));

            // validate form changes after advanced roi name was removed
            if (outputId) {
                performFormValidationForOutput(state, outputId);
            }
        }
    },
    [undoRoiCustomizationChanges.toString()]: (state: StoreState, action: { payload: { roiId: string, isGlobalRoi: boolean } }) => {
        const { roiId, isGlobalRoi } = action.payload;

        if (isGlobalRoi) {
            // it's NOT straightforward into what state global rois should be reverted to, so let's disable this for now. work-in-progress code
            // left in for posterity although it's not finished & doesn't do what it should
            throw new Error('Reverting changes to a global structure customization is NOT supported.');

            // // if this is a global roi, only revert changes to CURRENTLY COVERED rois
            // const globalRoi = state.globalRoiCustomizations.entities[roiId];
            // const originalGlobalRoi = state.originalGlobalRoiCustomizations.entities[roiId];
            // if (globalRoi && originalGlobalRoi) {
            //     const covered: string[] = [];
            //     for (const coveredRoiId of globalRoi.coveredRois) {
            //         const originalCoveredRoi = state.originalRoiCustomizations.entities[coveredRoiId];
            //         if (originalCoveredRoi) {
            //             roiCustomizationAdapter.upsertOne(state.roiCustomizations, originalCoveredRoi)
            //         }
            //     }

            //     globalRoiCustomizationAdapter.upsertOne(state.globalRoiCustomizations, originalGlobalRoi);
            // }
        } else {
            const originalRoi = state.originalRoiCustomizations.entities[roiId];
            if (originalRoi) {
                roiCustomizationAdapter.upsertOne(state.roiCustomizations, { ...originalRoi, scrollToView: false, globalRoiId: undefined });
                const output = originalRoi.modelCustomizationOutputId ? state.customizationOutputs.entities[originalRoi.modelCustomizationOutputId] : undefined;
                if (output && !output.rois.includes(roiId)) {
                    customizationOutputAdapter.updateOne(state.customizationOutputs, { id: output.id, changes: { rois: output.rois.filter(rId => rId !== roiId) } });
                }
                calculateGlobalRoiChangesForRegularRoi(state, roiId);

                // remove any validation errors
                customizationValidationErrorAdapter.removeOne(state.customizationValidationErrors, roiId);
                formValidationErrorAdapter.removeMany(state.formValidationErrors, getFormValidationErrorIdsForItem(state, roiId));

                // re-validate form changes after partial data in an output just got undoed
                if (output) {
                    performFormValidationForOutput(state, output.id);
                }
            }
        }
    },
    [undoAllCustomizationChanges.toString()]: (state: StoreState) => {
        modelAdapter.setAll(state.segmentationModels, state.originalSegmentationModels.entities as Record<string, SegmentationModel>);
        customizationBaseAdapter.setAll(state.customizationBases, state.originalCustomizationBases.entities as Record<string, ModelCustomizationBase>);
        customizationOutputAdapter.setAll(state.customizationOutputs, state.originalCustomizationOutputs.entities as Record<string, ModelCustomizationOutput>);
        modelCustomizationsMetadataAdapter.setAll(state.modelCustomizationsMetadata, state.originalModelCustomizationsMetadata.entities as Record<string, ModelCustomizationMetadataItem>);
        roiCustomizationAdapter.setAll(state.roiCustomizations, state.originalRoiCustomizations.entities as Record<string, RoiCustomization>);
        globalRoiCustomizationAdapter.setAll(state.globalRoiCustomizations, state.originalGlobalRoiCustomizations.entities as Record<string, GlobalRoiCustomization>);
        aeTitleRuleAdapter.setAll(state.aeTitleRules, state.originalAeTitleRules.entities as Record<string, AeTitleRule>);
        dicomRuleAdapter.setAll(state.dicomRules, state.originalDicomRules.entities as Record<string, DicomRule>);
        dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, state.originalDicomAttributeRules.entities as Record<string, DicomAttributeRule>);

        // remove any validation errors
        customizationValidationErrorAdapter.removeAll(state.customizationValidationErrors);
        formValidationErrorAdapter.removeAll(state.formValidationErrors);
        state.modelCustomizationSaveError = null;

        // calculate initial form validation errors
        for (const outputId of state.customizationOutputs.ids) {
            performFormValidationForOutput(state, outputId as string);
        }
    },
    [updateModelMetadataItem.toString()]: (state: StoreState, action: { payload: { metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean } }) => {
        const { metadataId, attribute, value, isUndoOperation } = action.payload;
        const metadataItem = state.modelCustomizationsMetadata.entities[metadataId];
        if (metadataItem && (attribute !== undefined || value !== undefined)) {
            const changes: Partial<ModelCustomizationMetadataItem> = { isModified: !isUndoOperation };
            if (attribute !== undefined) { changes['attribute'] = attribute; }
            if (value !== undefined) { changes['value'] = value; }

            modelCustomizationsMetadataAdapter.updateOne(state.modelCustomizationsMetadata, { id: metadataId, changes: changes });

            // update matching output filename prop if needed
            if (metadataItem.modelCustomizationOutputId !== undefined) {
                const currentAttribute = attribute !== undefined ? attribute : metadataItem.attribute;
                const currentValue = value !== undefined ? value : metadataItem.value;

                if (currentAttribute === METADATA_FILENAME_ATTRIBUTE) {
                    // update changed filename
                    customizationOutputAdapter.updateOne(state.customizationOutputs,
                        { id: metadataItem.modelCustomizationOutputId, changes: { filename: currentValue || '' } });
                } else if (metadataItem.attribute === METADATA_FILENAME_ATTRIBUTE) {
                    // attribute name was changed away from 'filename' -- try to find another matching value, then update
                    const output = state.customizationOutputs.entities[metadataItem.modelCustomizationOutputId];
                    if (output === undefined) { throw new Error(`Could not find customization output matching ID ${metadataItem.modelCustomizationOutputId}`); }
                    const matchingFilenameMetadataItems = Object.values(state.modelCustomizationsMetadata.entities)
                        .filter(m => m !== undefined && output.metadata.includes(m.id) && m.attribute === METADATA_FILENAME_ATTRIBUTE);
                    customizationOutputAdapter.updateOne(state.customizationOutputs,
                        { id: metadataItem.modelCustomizationOutputId, changes: { filename: matchingFilenameMetadataItems[0]?.value || '' } });
                }
            }

            if (!isUndoOperation) {
                setObjectAndAncestorsAsModified(state, [metadataId], CustomizationObjectType.Metadata);
            }
        }
    },
    [removeModelMetadataItem.toString()]: (state: StoreState, action: { payload: { metadataId: string } }) => {
        const { metadataId } = action.payload;
        const metadataItem = state.modelCustomizationsMetadata.entities[metadataId];
        if (metadataItem) {
            const output = metadataItem.modelCustomizationOutputId ? state.customizationOutputs.entities[metadataItem.modelCustomizationOutputId] : undefined;

            modelCustomizationsMetadataAdapter.removeOne(state.modelCustomizationsMetadata, metadataId);
            customizationValidationErrorAdapter.removeOne(state.customizationValidationErrors, metadataId);
            formValidationErrorAdapter.removeMany(state.formValidationErrors, getFormValidationErrorIdsForItem(state, metadataId));
            if (output) {
                customizationOutputAdapter.updateOne(state.customizationOutputs, { id: output.id, changes: { metadata: output.metadata.filter(mId => mId !== metadataId), isModified: true } })

                // update matching output filename prop if needed -- we might have multiple filename metadata entries so first try finding another match
                const matchingFilenameMetadataItems = Object.values(state.modelCustomizationsMetadata.entities)
                    .filter(m => m !== undefined && output.metadata.includes(m.id) && m.attribute === METADATA_FILENAME_ATTRIBUTE);
                customizationOutputAdapter.updateOne(state.customizationOutputs,
                    { id: output.id, changes: { filename: matchingFilenameMetadataItems[0]?.value || '' } });

                setObjectAndAncestorsAsModified(state, [output.id], CustomizationObjectType.CustomizationOutput);
            }
        }
    },
    /** Replaces an existing customization output and related other items with given input */
    [replaceModelCustomizationOutput.toString()]: (state: StoreState, action: { payload: { customizationOutputId: string, newOutputEntities: ModelCustomizationOutputEntities } }) => {

        const { customizationOutputId, newOutputEntities } = action.payload;
        const originalOutput = state.customizationOutputs.entities[customizationOutputId];

        if (!originalOutput) { throw new Error(`No original customization output (${customizationOutputId}) to replace!`); }
        if (newOutputEntities.customizationOutputs.length < 1) { throw new Error('No replacement customization output given'); }

        const originalRois = originalOutput.rois.map(rId => state.roiCustomizations.entities[rId]).filter(notEmpty);

        // currently only support replacing first output from given input even if we'd get several
        const newOutput = newOutputEntities.customizationOutputs[0];
        const newMetadata = newOutputEntities.modelCustomizationsMetadata.filter(m => newOutput.metadata.includes(m.id));
        const newRois = newOutputEntities.roiCustomizations.filter(r => newOutput.rois.includes(r.id));

        // modify key parts of the replacement output items to match the original. also mark everything as modified
        newOutput.id = originalOutput.id;
        newOutput.modelCustomizationBaseId = originalOutput.modelCustomizationBaseId;
        newOutput.filename = originalOutput.filename;
        newOutput.isModified = true;

        newMetadata.forEach(m => {
            if (m.attribute === METADATA_FILENAME_ATTRIBUTE) { m.value = originalOutput.filename; }
            m.modelCustomizationOutputId = originalOutput.id;
            m.isModified = true;
        });

        newRois.forEach(r => {
            r.modelCustomizationOutputId = originalOutput.id;
            r.isModified = true;
        });

        // remove old metadata, roi items, and related form validation errors
        modelCustomizationsMetadataAdapter.removeMany(state.modelCustomizationsMetadata, originalOutput.metadata);
        roiCustomizationAdapter.removeMany(state.roiCustomizations, originalOutput.rois);
        const removedIds: string[] = [
            customizationOutputId,
            ...originalOutput.rois,
            ...originalOutput.metadata,
        ];
        formValidationErrorAdapter.removeMany(state.formValidationErrors, getFormValidationErrorIdsForItems(state, removedIds));

        // add new items
        modelCustomizationsMetadataAdapter.addMany(state.modelCustomizationsMetadata, newMetadata);
        roiCustomizationAdapter.addMany(state.roiCustomizations, newRois);

        // swap in the new customization output
        customizationOutputAdapter.setOne(state.customizationOutputs, newOutput);

        // mark ancestors as modified (start with customization base as the output was already marked earlier)
        setObjectAndAncestorsAsModified(state, [newOutput.modelCustomizationBaseId], CustomizationObjectType.CustomizationBase);

        // also remove links to just deleted ROIs from global ROIs
        const allGlobalRois = Object.values(state.globalRoiCustomizations.entities).filter(notEmpty);
        for (const roi of originalRois) {
            if (roi.globalRoiId) {
                // find and modify global roi where this roi is covered
                const globalRoi = state.globalRoiCustomizations.entities[roi.globalRoiId];
                if (globalRoi && globalRoi.coveredRois.includes(roi.id)) {
                    if (globalRoi) {
                        globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                            id: globalRoi.id, changes: { coveredRois: globalRoi.coveredRois.filter(cr => cr !== roi.id) }
                        });
                    }
                }
            } else {
                // find and modify global roi where this roi is excluded
                const globalRoi = allGlobalRois.find(g => g.operation === roi.operation && g.excludedRois.includes(roi.id));
                if (globalRoi) {
                    if (globalRoi) {
                        globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                            id: globalRoi.id, changes: { excludedRois: globalRoi.excludedRois.filter(er => er !== roi.id) }
                        });
                    }
                }
            }
        };

        // recalculate global ROIs
        newRois.forEach(r => calculateGlobalRoiChangesForRegularRoi(state, r.id));
    },
    [saveModelCustomizationStart.toString()]: (state: StoreState) => {
        state.isModelCustomizationSavingInProgress = true;
    },
    [saveModelCustomizationFinish.toString()]: (state: StoreState, action: { payload: { saveWasSuccessful: boolean, errorMessage?: string, error?: Error } }) => {
        const { saveWasSuccessful, errorMessage, error } = action.payload;
        state.isModelCustomizationSavingInProgress = false;

        // remove existing validation errors -- we may add new ones more in just a moment
        customizationValidationErrorAdapter.removeAll(state.customizationValidationErrors);

        // DON'T remove form validation errors though -- we may have warnings there that still apply
        // formValidationErrorAdapter.removeAll(state.formValidationErrors);

        if (saveWasSuccessful) {
            // set everything back to unmodified & store as the new original entities
            Object.values(state.segmentationModels.entities).forEach(m => { if (m !== undefined) { m.isModified = false } });
            Object.values(state.customizationBases.entities).forEach(c => { if (c !== undefined) { c.isModified = false } });
            Object.values(state.customizationOutputs.entities).forEach(c => { if (c !== undefined) { c.isModified = false } });
            Object.values(state.modelCustomizationsMetadata.entities).forEach(md => { if (md !== undefined) { md.isModified = false } });
            Object.values(state.roiCustomizations.entities).forEach(r => { if (r !== undefined) { r.isModified = false } });
            Object.values(state.globalRoiCustomizations.entities).forEach(gr => { if (gr !== undefined) { gr.isModified = false } });
            Object.values(state.aeTitleRules.entities).forEach(r => { if (r !== undefined) { r.isModified = false; r.isNew = false; } });
            Object.values(state.dicomRules.entities).forEach(r => { if (r !== undefined) { r.isModified = false; r.isNew = false; } });
            Object.values(state.dicomAttributeRules.entities).forEach(r => { if (r !== undefined) { r.isModified = false; r.isNew = false; } });

            modelAdapter.setAll(state.originalSegmentationModels, state.segmentationModels.entities as Record<string, SegmentationModel>);
            customizationBaseAdapter.setAll(state.originalCustomizationBases, state.customizationBases.entities as Record<string, ModelCustomizationBase>);
            customizationOutputAdapter.setAll(state.originalCustomizationOutputs, state.customizationOutputs.entities as Record<string, ModelCustomizationOutput>);
            modelCustomizationsMetadataAdapter.setAll(state.originalModelCustomizationsMetadata, state.modelCustomizationsMetadata.entities as Record<string, ModelCustomizationMetadataItem>);
            roiCustomizationAdapter.setAll(state.originalRoiCustomizations, state.roiCustomizations.entities as Record<string, RoiCustomization>);
            globalRoiCustomizationAdapter.setAll(state.originalGlobalRoiCustomizations, state.globalRoiCustomizations.entities as Record<string, GlobalRoiCustomization>);
            aeTitleRuleAdapter.setAll(state.originalAeTitleRules, state.aeTitleRules.entities as Record<string, AeTitleRule>);
            dicomRuleAdapter.setAll(state.originalDicomRules, state.dicomRules.entities as Record<string, DicomRule>);
            dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, state.dicomAttributeRules.entities as Record<string, DicomAttributeRule>);

        }

        state.modelCustomizationSaveError = errorMessage !== undefined ? errorMessage : null;

        // collect save errors
        try {
            if (!saveWasSuccessful && error !== undefined && isBackendValidationError(error)) {
                const backendValidationErrors = convertCustomizationValidationErrorToStoreObjects(state, error);
                if (backendValidationErrors.length > 0) {
                    // apply new validation errors and override whatever error message we received from sagas
                    customizationValidationErrorAdapter.addMany(state.customizationValidationErrors, backendValidationErrors);
                    state.modelCustomizationSaveError = 'Could not save customizations because they have invalid entries. Please fix any validation errors and try again.';
                }
            }
        }
        catch (ex) {
            const message: string | undefined = get(ex, 'message', undefined);
            state.modelCustomizationSaveError = `An error occurred when trying to parse validation error message${message ? `: ${message}` : '.'}`
        }
    },
    [resetModelCustomizationStart.toString()]: (state: StoreState) => {
        state.isModelCustomizationDeleteInProgress = true;
    },
    [resetModelCustomizationFinish.toString()]: (state: StoreState) => {
        state.isModelCustomizationDeleteInProgress = false;
    },
    [resetSingleCustomizationOutputStart.toString()]: (state: StoreState) => {
        state.isCustomizationOutputResetInProgress = true;
    },
    [resetSingleCustomizationOutputFinish.toString()]: (state: StoreState, action: { payload: { resetWasSuccessful: boolean, errorMessage?: string, error?: Error }}) => {
        state.isCustomizationOutputResetInProgress = false;
        state.modelCustomizationDataError = action.payload.errorMessage || null;
    },
    [includeDeprecatedModels.toString()]: (state: StoreState) => {
        state.areDeprecatedModelsShown = true;
    },
    [excludeDeprecatedModels.toString()]: (state: StoreState) => {
        state.areDeprecatedModelsShown = false;
    },
    [includeUserHiddenModels.toString()]: (state: StoreState) => {
        state.areUserHiddenModelsShown = true;
    },
    [excludeUserHiddenModels.toString()]: (state: StoreState) => {
        state.areUserHiddenModelsShown = false;
    },
    [includeNonLicensedModels.toString()]: (state: StoreState) => {
        state.areNonLicensedModelsShown = true;
    },
    [excludeNonLicensedModels.toString()]: (state: StoreState) => {
        state.areNonLicensedModelsShown = false;
    },
    [duplicateCustomizationBase.toString()]: (state: StoreState, action: { payload: { customizationBaseId: string, newCustomizationName: string } }) => {
        const { customizationBaseId, newCustomizationName } = action.payload;

        const sourceCustomizationBase = state.customizationBases.entities[customizationBaseId];
        if (!sourceCustomizationBase) { throw new Error(`Could not retrieve customization base with id ${customizationBaseId}`); }
        const parentSegmentationModel = state.segmentationModels.entities[sourceCustomizationBase.segmentationModelId];
        if (!parentSegmentationModel) { throw new Error(`Could not retrieve parent segmentation model with id ${sourceCustomizationBase.segmentationModelId}`); }

        const newCustomizationBaseId = generateNewId();

        // collect and map all ids we duplicate here so we can also duplicate related form errors to new targets
        const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: customizationBaseId, targetId: newCustomizationBaseId }];

        // duplicate customization outputs (note: triggers are NOT duplicated)
        const newOutputs: ModelCustomizationOutput[] = [];
        let newRois: RoiCustomization[] = [];
        let newMetadata: ModelCustomizationMetadataItem[] = [];
        let globalRoiChanges: Update<GlobalRoiCustomization>[] = [];
        for (const outputId of sourceCustomizationBase.outputs) {
            const sourceCustomizationOutput = state.customizationOutputs.entities[outputId];
            if (!sourceCustomizationOutput) { throw new Error(`Could not retrieve customization output with id ${outputId}`); }
            const duplicationEntities = duplicateCustomizationOutputFromStore(state, sourceCustomizationOutput, undefined, newCustomizationBaseId);
            newOutputs.push(duplicationEntities.duplicatedOutput);
            newRois = newRois.concat(duplicationEntities.newRois);
            newMetadata = newMetadata.concat(duplicationEntities.newMetadata);
            globalRoiChanges = globalRoiChanges.concat(duplicationEntities.globalRoiChanges);

            duplicatedIds.push(...duplicationEntities.duplicatedIds);
        }

        // duplicate customization base
        const duplicatedCustomizationBase = createNewModelCustomizationBase(
            newCustomizationName,
            sourceCustomizationBase.description,
            newOutputs.map(o => o.id),
            [],
            [],
            sourceCustomizationBase.segmentationModelId,
            newCustomizationBaseId,
            true
        );

        // store everything
        customizationBaseAdapter.addOne(state.customizationBases, duplicatedCustomizationBase);
        customizationOutputAdapter.addMany(state.customizationOutputs, newOutputs);
        modelCustomizationsMetadataAdapter.addMany(state.modelCustomizationsMetadata, newMetadata);
        roiCustomizationAdapter.addMany(state.roiCustomizations, newRois);
        globalRoiCustomizationAdapter.updateMany(state.globalRoiCustomizations, globalRoiChanges);
        for (const { id } of globalRoiChanges) {
            // make sure global roi covered and excluded items are in order
            ensureGlobalRoiItemsAreInAlphabeticalOrder(state, id as string);
        }

        modelAdapter.updateOne(state.segmentationModels, {
            id: parentSegmentationModel.id,
            changes: { customizations: parentSegmentationModel.customizations.concat(duplicatedCustomizationBase.id) }
        });

        setObjectAndAncestorsAsModified(state, [parentSegmentationModel.id], CustomizationObjectType.Model);

        // focus on the new duplicate
        if (newOutputs.length > 0) {
            state.modelCustomizationPageFocusId = newOutputs[0].id;
        }

        // collect and duplicate any form errors we may have -- they still apply (backend errors are not worth duplicating, they'll re-appear during save)
        const duplicatedErrors = duplicateFormValidationErrors(state, duplicatedIds);
        if (duplicatedErrors.length > 0) {
            formValidationErrorAdapter.addMany(state.formValidationErrors, duplicatedErrors);
        }
    },
    [duplicateCustomizationOutput.toString()]: (state: StoreState, action: { payload: { customizationOutputId: string, newFilename: string } }) => {
        const { customizationOutputId, newFilename } = action.payload;

        const sourceCustomizationOutput = state.customizationOutputs.entities[customizationOutputId];
        if (!sourceCustomizationOutput) { throw new Error(`Could not retrieve customization output with id ${customizationOutputId}`); }
        const sourceCustomizationBase = state.customizationBases.entities[sourceCustomizationOutput.modelCustomizationBaseId];
        if (!sourceCustomizationBase) { throw new Error(`Could not retrieve customization base with id ${sourceCustomizationOutput.modelCustomizationBaseId}`); }

        const duplicationEntities = duplicateCustomizationOutputFromStore(state, sourceCustomizationOutput, newFilename);

        // collect and map all ids we duplicate here so we can also duplicate related form errors to new targets
        const duplicatedIds: DuplicatedIdMap[] = [...duplicationEntities.duplicatedIds];

        // store everything
        customizationOutputAdapter.addOne(state.customizationOutputs, duplicationEntities.duplicatedOutput);
        modelCustomizationsMetadataAdapter.addMany(state.modelCustomizationsMetadata, duplicationEntities.newMetadata);
        roiCustomizationAdapter.addMany(state.roiCustomizations, duplicationEntities.newRois);
        globalRoiCustomizationAdapter.updateMany(state.globalRoiCustomizations, duplicationEntities.globalRoiChanges);
        for (const { id } of duplicationEntities.globalRoiChanges) {
            // make sure global roi covered and excluded items are in order
            ensureGlobalRoiItemsAreInAlphabeticalOrder(state, id as string);
        }

        customizationBaseAdapter.updateOne(state.customizationBases, {
            id: sourceCustomizationBase.id,
            changes: { outputs: sourceCustomizationBase.outputs.concat(duplicationEntities.duplicatedOutput.id) }
        });

        setObjectAndAncestorsAsModified(state, [sourceCustomizationBase.id], CustomizationObjectType.CustomizationBase);

        // focus on the new duplicate
        state.modelCustomizationPageFocusId = duplicationEntities.duplicatedOutput.id;

        // collect and duplicate any form errors we may have -- they still apply (backend errors are not worth duplicating, they'll re-appear during save)
        const duplicatedErrors = duplicateFormValidationErrors(state, duplicatedIds);
        if (duplicatedErrors.length > 0) {
            formValidationErrorAdapter.addMany(state.formValidationErrors, duplicatedErrors);
        }
    },
    [renameCustomizationModel.toString()]: (state: StoreState, action: { payload: { customizationBaseId: string, name: string } }) => {
        const { customizationBaseId, name } = action.payload;
        customizationBaseAdapter.updateOne(state.customizationBases, { id: customizationBaseId, changes: { isModified: true, customizationName: name } });
        setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);
    },
    [removeCustomizationBase.toString()]: (state: StoreState, action: { payload: { customizationBaseId: string } }) => {
        const { customizationBaseId } = action.payload;
        const customization = state.customizationBases.entities[customizationBaseId];

        if (customization === undefined) {
            throw new Error(`Could not find customization base ${customizationBaseId}`);
        }

        if (customization.customizationName === DEFAULT_MODEL_CUSTOMIZATION_NAME) {
            throw new Error('Default customizations cannot be deleted.');
        }

        // mark base model as modified so we know we need to save the configuration
        setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);

        const outputs = customization.outputs.map(oId => state.customizationOutputs.entities[oId]);
        const globalRoiChanges: Update<GlobalRoiCustomization>[] = [];
        const roiIdsToBeRemoved: string[] = [];
        const metadataIdsToBeRemoved: string[] = [];
        const dicomAttributeIdsToBeRemoved: string[] = [];

        for (const output of outputs) {
            const removedOutputEntities = removeCustomizationOutputFromStore(state, output, customizationBaseId);
            globalRoiChanges.push(...removedOutputEntities.globalRoiChanges);
            roiIdsToBeRemoved.push(...removedOutputEntities.roiIdsToBeRemoved);
            metadataIdsToBeRemoved.push(...removedOutputEntities.metadataIdsToBeRemoved);
        }


        // get dicom attribute rules to remove
        for (const dicomRuleId of customization.dicomRules) {
            const dicomRule = state.dicomRules.entities[dicomRuleId];
            if (dicomRule === undefined) {
                throw new Error(`Could not retrieve dicom rule ${dicomRuleId} for customization base ${customizationBaseId}`);
            }
            dicomAttributeIdsToBeRemoved.push(...dicomRule.dicomAttributes);
        }

        const model = state.segmentationModels.entities[customization.segmentationModelId];
        if (model === undefined) {
            throw new Error(`Could not retrieve segmentation model parent ${customization.segmentationModelId} for customization base ${customizationBaseId}`);
        }

        // update global rois
        globalRoiCustomizationAdapter.updateMany(state.globalRoiCustomizations, globalRoiChanges);

        // delete all matching items
        roiCustomizationAdapter.removeMany(state.roiCustomizations, roiIdsToBeRemoved);
        modelCustomizationsMetadataAdapter.removeMany(state.modelCustomizationsMetadata, metadataIdsToBeRemoved);
        customizationOutputAdapter.removeMany(state.customizationOutputs, customization.outputs);
        dicomAttributeRuleAdapter.removeMany(state.dicomAttributeRules, dicomAttributeIdsToBeRemoved);
        aeTitleRuleAdapter.removeMany(state.aeTitleRules, customization.aeTitleRules);
        dicomRuleAdapter.removeMany(state.dicomRules, customization.dicomRules);
        customizationBaseAdapter.removeOne(state.customizationBases, customizationBaseId);

        // update segmentation model
        modelAdapter.updateOne(state.segmentationModels, { id: customization.segmentationModelId, changes: { customizations: model.customizations.filter(c => c !== customizationBaseId) } });

        // no need to specifically remove validation errors here -- they will not show up in UI and will be cleared from store during next save operation
        // however **form** validation errors should be removed
        const removedIds: string[] = [
            customizationBaseId,
            ...customization.dicomRules,
            ...customization.aeTitleRules,
            ...dicomAttributeIdsToBeRemoved,
            ...customization.outputs,
            ...metadataIdsToBeRemoved,
            ...roiIdsToBeRemoved,
        ];
        formValidationErrorAdapter.removeMany(state.formValidationErrors, getFormValidationErrorIdsForItems(state, removedIds));
    },
    [removeCustomizationOutput.toString()]: (state: StoreState, action: { payload: { customizationOutputId: string } }) => {
        const { customizationOutputId } = action.payload;

        const output = state.customizationOutputs.entities[customizationOutputId];
        if (output === undefined) { throw new Error(`Could not find customization output ${customizationOutputId}`); }

        const customization = state.customizationBases.entities[output.modelCustomizationBaseId];
        if (customization === undefined) { throw new Error(`Could not find customization base ${output.modelCustomizationBaseId} for output ${customizationOutputId}`); }

        // mark parent objects as modified so we know we need to save the configuration
        setObjectAndAncestorsAsModified(state, [customizationOutputId], CustomizationObjectType.CustomizationOutput);

        const removedOutputEntities = removeCustomizationOutputFromStore(state, output, output.modelCustomizationBaseId);

        // update global rois
        globalRoiCustomizationAdapter.updateMany(state.globalRoiCustomizations, removedOutputEntities.globalRoiChanges);

        // delete all matching items
        roiCustomizationAdapter.removeMany(state.roiCustomizations, removedOutputEntities.roiIdsToBeRemoved);
        modelCustomizationsMetadataAdapter.removeMany(state.modelCustomizationsMetadata, removedOutputEntities.metadataIdsToBeRemoved);
        customizationOutputAdapter.removeOne(state.customizationOutputs, customizationOutputId);

        // update customization base
        customizationBaseAdapter.updateOne(state.customizationBases, { id: output.modelCustomizationBaseId, changes: { outputs: customization.outputs.filter(o => o !== customizationOutputId) } });

        // no need to specifically remove validation errors here -- they will not show up in UI and will be cleared from store during next save operation
        // however form validation errors should be removed
        const removedIds: string[] = [
            customizationOutputId,
            ...removedOutputEntities.roiIdsToBeRemoved,
            ...removedOutputEntities.metadataIdsToBeRemoved,
        ];
        formValidationErrorAdapter.removeMany(state.formValidationErrors, getFormValidationErrorIdsForItems(state, removedIds));
    },






    [fetchTextEditorFiles.toString()]: (state: StoreState) => {
        state.isLoadingTextEditorFilePaths = true;
    },
    [setFoundTextEditorFilePaths.toString()]: (state: StoreState, action: { payload: { foundFiles: TextEditorFile[] } }) => {
        const { foundFiles } = action.payload;
        state.isLoadingTextEditorFilePaths = false;
        if (get(state, 'textEditorFiles', []).length > 0) {
            // do not allow overwriting of files with unsaved changes
            foundFiles.forEach(ff => {
                const matchingOldFile = state.textEditorFiles.find(f => f.filePath === ff.filePath);
                if (matchingOldFile !== undefined && !matchingOldFile.isModified) {
                    // if an old file is found from the store and it is NOT modified, replace it with the new file
                    const index = state.textEditorFiles.indexOf(matchingOldFile);
                    state.textEditorFiles[index] = ff;
                } else if (matchingOldFile === undefined) {
                    // if an old file is not found, append the new file to the array
                    state.textEditorFiles.push(ff);
                }
            });
        } else {
            state.textEditorFiles = foundFiles;
        }

        // finally, make sure the files are sorted in alphabetical order
        state.textEditorFiles = sortBy(state.textEditorFiles, f => f.filePath);
    },
    [fetchTextEditorFileContents.toString()]: (state: StoreState, action: { payload: { filePath: string } }) => {
        const { filePath } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            file.isLoadingFile = true;
        }
    },
    [setTextEditorFileContents.toString()]: (state: StoreState, action: { payload: { filePath: string, contents: string } }) => {
        const { filePath, contents } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            file.isLoadingFile = false;
            file.fileContents = contents;
            file.originalFileContents = contents;
            file.isLoaded = true;
            file.isModified = false;
        }
    },
    [modifyTextEditorFile.toString()]: (state: StoreState, action: { payload: { filePath: string, newContents: string } }) => {
        const { filePath, newContents } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            file.fileContents = newContents;
            file.isModified = true;
        }
    },
    [openTextEditorFileForEditing.toString()]: (state: StoreState, action: { payload: { filePath: string } }) => {
        const { filePath } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            // not the most elegant way to handle this, but it keeps the complexity low
            file.isModified = true;
        }
    },
    [closeTextEditorFileForEditing.toString()]: (state: StoreState, action: { payload: { filePath: string } }) => {
        const { filePath } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            // not the most elegant way to handle this, but it keeps the complexity low
            file.isModified = false;
            file.errorSavingFile = null;
        }
    },
    [undoTextEditorFileChanges.toString()]: (state: StoreState, action: { payload: { filePath: string } }) => {
        const { filePath } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            file.fileContents = file.originalFileContents;
            file.errorSavingFile = null;
            // note that isModified prop is left intact
        }
    },
    [saveTextEditorFileContentsStart.toString()]: (state: StoreState, action: { payload: { filePath: string, newContents: string } }) => {
        const { filePath } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            file.errorSavingFile = null;
        }
    },
    [saveTextEditorFileContentsFinish.toString()]: (state: StoreState, action: { payload: { filePath: string, newContents: string | null, error: string | undefined } }) => {
        const { filePath, newContents, error } = action.payload;
        const file = state.textEditorFiles.find(f => f.filePath === filePath);
        if (file) {
            if (newContents !== null) {
                // saving the file was successful
                file.originalFileContents = newContents;
                if (file.fileContents === newContents) { file.isModified = false; }
            } else {
                file.errorSavingFile = error !== undefined ? error : 'Unspecified error.';
            }
        }
    },
    [setLicenseStatus.toString()]: (state: StoreState, action: { payload: { licenseStatus: LicenseStatus[] | null, error: string | undefined } }) => {
        const { licenseStatus, error } = action.payload;

        if (licenseStatus !== null) {
            state.licenseStatus = licenseStatus;
        }

        state.licenseStatusError = error !== undefined ? error : null;
    },
    [updateLicenseStart.toString()]: (state: StoreState) => {
        state.isUpdatingLicense = true;
        state.licenseUpdateError = null;
    },
    [updateLicenseFinish.toString()]: (state: StoreState, action: { payload: { success: boolean, error: string | undefined } }) => {
        const { success, error } = action.payload;

        state.isUpdatingLicense = false;
        state.licenseUpdateError = error !== undefined ? error : null;

        if (success) {
            state.successfulLicenseUpdatesCount++;
        }
    },
    [setDaemonConfigs.toString()]: (state: StoreState, action: { payload: { daemonConfigs: DaemonConfig[] | null, error?: string } }) => {
        const { daemonConfigs, error } = action.payload;

        if (daemonConfigs !== null) {
            state.daemonConfigs = daemonConfigs;
        }

        // clear any errors
        daemonValidationErrorAdapter.removeAll(state.daemonValidationErrors);

        state.daemonConfigsError = error !== undefined ? error : null;
    },
    [setDaemonConfig.toString()]: (state: StoreState, action: { payload: { daemonConfig: DaemonConfig | null, error?: string } }) => {
        const { daemonConfig, error } = action.payload;

        if (daemonConfig !== null && state.daemonConfigs) {
            // put fetched daemon config in state, replacing a matching entry if found
            const index = state.daemonConfigs.findIndex(d => d.sessionId === daemonConfig.sessionId);
            if (index >= 0) {
                state.daemonConfigs[index] = daemonConfig;
            } else {
                state.daemonConfigs.push(daemonConfig);
            }
        }

        // clear any errors
        daemonValidationErrorAdapter.removeAll(state.daemonValidationErrors);

        state.daemonConfigsError = error !== undefined ? error : null;
    },
    [unsetDaemonConfig.toString()]: (state: StoreState, action: { payload: { daemonSessionId: string } }) => {
        const { daemonSessionId } = action.payload;

        if (state.daemonConfigs) {
            // remove matching daemon client ID if found
            const index = state.daemonConfigs.findIndex(d => d.sessionId === daemonSessionId);
            if (index >= 0) {
                state.daemonConfigs.splice(index, 1);
            }
        }
    },
    [saveDaemonConfigStart.toString()]: (state: StoreState, action: { payload: { appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig } }) => {
        // TODO: consider using input arguments to specify which daemon config is being saved -- currently they are unused and you can only
        // save one daemon config at a time (which is probably fine for almost any use case)
        state.isDaemonConfigSaveInProgress = true;
    },
    [saveDaemonConfigFinish.toString()]: (state: StoreState, action: { payload: { appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig, saveWasSuccessful: boolean, errorMessage?: string, error?: Error } }) => {
        const { appClient, daemonConfig, saveWasSuccessful, errorMessage, error } = action.payload;

        state.isDaemonConfigSaveInProgress = false;
        state.daemonConfigSaveError = null;

        // clear any errors
        daemonValidationErrorAdapter.removeAll(state.daemonValidationErrors);

        if (saveWasSuccessful) {
            // store internal version of saved daemon config into redux collection
            if (state.daemonConfigs && isEqual(state.currentAppClient, appClient)) {
                const index = state.daemonConfigs.findIndex(d => d.sessionId === daemonConfig.sessionId);
                if (index >= 0) {
                    state.daemonConfigs[index] = daemonConfig;
                }
            }
        } else {
            console.error(error);
            state.daemonConfigSaveError = errorMessage ? errorMessage : 'Saving daemon configuration failed.';

            // collect save errors
            try {
                if (isBackendValidationError(error)) {
                    const daemonValidationErrors = convertDaemonValidationErrorsToStoreObjects(daemonConfig.sessionId, error);
                    if (daemonValidationErrors.length > 0) {
                        // apply new validation errors and override whatever error message we received from sagas
                        daemonValidationErrorAdapter.addMany(state.daemonValidationErrors, daemonValidationErrors);
                        state.daemonConfigSaveError = 'Could not save daemon configuration because there are invalid entries. Please fix any validation errors and try again.';
                    }
                }
            }
            catch (ex) {
                const message: string | undefined = get(ex, 'message', undefined);
                state.daemonConfigSaveError = `An error occurred when trying to parse validation error message${message ? `: ${message}` : '.'}`
            }
        }
    },
    [resetDaemonConfigStart.toString()]: (state: StoreState) => {
        state.isDaemonConfigResetInProgress = true;
    },
    [resetDaemonConfigFinish.toString()]: (state: StoreState) => {
        // clear any errors
        daemonValidationErrorAdapter.removeAll(state.daemonValidationErrors);

        state.isDaemonConfigResetInProgress = false;
    },
    [removeDaemonConfigStart.toString()]: (state: StoreState) => {
        state.isDaemonConfigDeleteInProgress = true;
    },
    [removeDaemonConfigFinish.toString()]: (state: StoreState) => {
        // clear any errors
        daemonValidationErrorAdapter.removeAll(state.daemonValidationErrors);

        state.isDaemonConfigDeleteInProgress = false;
    },
    [setLabeling.toString()]: (state: StoreState, action: { payload: { productLabel: Labeling | null, error: string | undefined } }) => {
        const { productLabel, error } = action.payload;

        if (productLabel !== null) {
            state.productLabel = productLabel;
        }

        state.productLabelError = error !== undefined ? error : null;
    },

    [addAppAuthState.toString()]: (state: StoreState, action: { payload: { appAuthState: AppAuthState } }) => {
        const { appAuthState } = action.payload;
        if (state.appAuthStates.find(a => a.appAuthName === appAuthState.appAuthName) === undefined) {
            state.appAuthStates.push(appAuthState);
        }
    },

    [startAppAuthLogin.toString()]: (state: StoreState, action: { payload: { appAuthName: string } }) => {
        const { appAuthName } = action.payload;
        const authState = state.appAuthStates.find(a => a.appAuthName === appAuthName);
        if (authState && authState.logInProcessState !== LogInProcessState.LoggedIn) {
            authState.logInProcessState = LogInProcessState.LoggingInProgress;
        }
    },

    [setLoginError.toString()]: (state: StoreState, action: { payload: { loginError: LoginError | null } }) => {
        const { loginError } = action.payload;
        state.loginError = loginError;
    },

    [logInSuccess.toString()]: (state: StoreState, action: { payload: { appAuthName: string } }) => {
        const { appAuthName } = action.payload;
        const authState = state.appAuthStates.find(a => a.appAuthName === appAuthName);
        if (authState !== undefined) {
            authState.logInProcessState = LogInProcessState.LoggedIn;
        }
    },

    [setUserSettings.toString()]: (state: StoreState, action: { payload: { userSettings: UserSettings | null, error?: string } }) => {
        const { userSettings, error } = action.payload;

        if (userSettings !== null) {
            state.userSettings = userSettings;
        }

        state.userSettingsError = error !== undefined ? error : null;
    },
    [setAccessRights.toString()]: (state: StoreState, action: { payload: { accessRights: AccessRights | null, error?: string } }) => {
        const { accessRights, error } = action.payload;

        if (accessRights !== null) {
            state.accessRights = accessRights;
        }

        state.accessRightsError = error !== undefined ? error : null;
    },

    [updateUserSettings.toString()]: (state: StoreState, action: { payload: { settingsToUpdate: Partial<UserSettings> } }) => {
        const { settingsToUpdate } = action.payload;
        state.userSettings = Object.assign({}, state.userSettings, settingsToUpdate);
    },

    [setClientList.toString()]: (state: StoreState, action: { payload: { clientList: MVisionAppClient[] | null, error?: string } }) => {
        const { clientList, error } = action.payload;

        if (clientList !== null) {
            state.clientList = clientList;
        }

        if (!error) {
            state.isClientListFetched = true;
        }

        state.clientListError = error !== undefined ? error : null;
    },

    [setCurrentAppClient.toString()]: (state: StoreState, action: { payload: { userId: string | undefined } }) => {
        const { userId } = action.payload;

        const newAppClient = userId === undefined ? undefined : state.clientList ? state.clientList.find(cl => cl.userId === userId) : undefined;
        state.currentAppClient = newAppClient;
    },

    [startFetchingClientData.toString()]: (state: StoreState) => {
        state.isFetchingClientData = true;
    },

    [finishFetchingClientData.toString()]: (state: StoreState) => {
        state.isFetchingClientData = false;
    },





    /**************************** */
    /**                           */
    /**                           */
    /** MODEL SELECTION REDUCERS  */
    /**                           */
    /**                           */
    /**************************** */

    // (TODO: split all reducers into separate files with the help of createSlice(), but that's a bit of a bigger task)

    [changeAeTitleRuleAction.toString()]: (state: StoreState, action: { payload: { id: string, action: string } }) => {
        const { payload } = action;
        const previousActionName = state.aeTitleRules.entities[payload.id]?.action;
        aeTitleRuleAdapter.updateOne(state.aeTitleRules, { id: payload.id, changes: { action: payload.action, isModified: true } });

        setObjectAndAncestorsAsModified(state, [payload.id], CustomizationObjectType.AeTitleRule);

        if (previousActionName !== undefined) {
            validateAeTitleRuleActionUniqueness(state, previousActionName);
        }
        validateAeTitleRuleActionUniqueness(state, payload.action);
    },

    [changeDicomAttributeRuleAction.toString()]: (state: StoreState, action: { payload: { id: string, dicomAttribute: string, dicomValue: string } }) => {
        const { id, dicomAttribute, dicomValue } = action.payload;
        const dicomAttributeRule = state.dicomAttributeRules.entities[id];
        if (dicomAttributeRule === undefined) { throw new Error(`Could not find DICOM attribute rule with id ${id}`); }
        const dicomRuleId = dicomAttributeRule.parentDicomRuleId;

        // save changes to the object immediately
        dicomAttributeRuleAdapter.updateOne(state.dicomAttributeRules,
            {
                id: id,
                changes: {
                    attribute: dicomAttribute,
                    value: dicomValue,
                    isModified: true,
                }
            });

        setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.DicomAttributeRule);

        // perform validation AFTER changes have been saved
        validateDicomRuleAndAttributes(state, dicomRuleId);
    },

    [addAeTitleRule.toString()]: (state: StoreState, action: { payload: { aeTitleRule: AeTitleRule } }) => {
        const { aeTitleRule } = action.payload;

        assertIsDefined(aeTitleRule.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for new AE Title rule');

        const customization = state.customizationBases.entities[aeTitleRule.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${aeTitleRule.modelCustomizationBaseId}`);

        aeTitleRuleAdapter.addOne(state.aeTitleRules, aeTitleRule);
        customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { aeTitleRules: customization.aeTitleRules.concat(aeTitleRule.id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [aeTitleRule.id], CustomizationObjectType.AeTitleRule);

        validateAeTitleRuleActionUniqueness(state, aeTitleRule.action);
    },

    [addDicomRule.toString()]: (state: StoreState, action: { payload: { dicomRule: DicomRule } }) => {
        const { dicomRule } = action.payload;

        assertIsDefined(dicomRule.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for new DICOM rule');

        const customization = state.customizationBases.entities[dicomRule.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${dicomRule.modelCustomizationBaseId}`);

        dicomRuleAdapter.addOne(state.dicomRules, dicomRule);
        customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { dicomRules: customization.dicomRules.concat(dicomRule.id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [dicomRule.id], CustomizationObjectType.DicomRule);
    },

    [addDicomAttributeRule.toString()]: (state: StoreState, action: { payload: { dicomAttributeRule: DicomAttributeRule } }) => {
        const { dicomAttributeRule } = action.payload;

        const parentDicomRule = state.dicomRules.entities[dicomAttributeRule.parentDicomRuleId];
        if (parentDicomRule === undefined) { throw new Error(`Could not find parent DicomRule object with ID ${dicomAttributeRule.parentDicomRuleId}`); }
        if (!parentDicomRule.dicomAttributes.includes(dicomAttributeRule.id)) {
            dicomRuleAdapter.updateOne(state.dicomRules, {
                id: dicomAttributeRule.parentDicomRuleId,
                changes: {
                    dicomAttributes: parentDicomRule.dicomAttributes.concat(dicomAttributeRule.id),
                    isModified: true
                }
            });
        }

        dicomAttributeRuleAdapter.addOne(state.dicomAttributeRules, dicomAttributeRule);

        // mark matching parent entities as modified
        setObjectAndAncestorsAsModified(state, [dicomAttributeRule.id], CustomizationObjectType.DicomAttributeRule);

        validateDicomRuleAndAttributes(state, dicomAttributeRule.parentDicomRuleId);
    },

    [removeAeTitleRule.toString()]: (state: StoreState, action: { payload: { id: string } }) => {
        const { id } = action.payload;
        const aeTitleRuleToRemove = state.aeTitleRules.entities[id];
        aeTitleRuleAdapter.removeOne(state.aeTitleRules, id);

        // update matching parent objects
        assertIsDefined(aeTitleRuleToRemove, 'AE title rule about to be removed is not defined');
        assertIsDefined(aeTitleRuleToRemove.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for the AE title rule about to be removed');
        const customization = state.customizationBases.entities[aeTitleRuleToRemove.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${aeTitleRuleToRemove.modelCustomizationBaseId}`);
        customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { aeTitleRules: pull(customization.aeTitleRules, id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [customization.id], CustomizationObjectType.CustomizationBase);

        validateAeTitleRuleActionUniqueness(state, aeTitleRuleToRemove.action);
    },

    [removeDicomRule.toString()]: (state: StoreState, action: { payload: { id: string } }) => {
        const { id } = action.payload;
        const dicomRuleToRemove = state.dicomRules.entities[id];
        if (dicomRuleToRemove === undefined) { throw new Error(`Could not find DICOM rule ${id} to remove`); }
        dicomAttributeRuleAdapter.removeMany(state.dicomAttributeRules, dicomRuleToRemove.dicomAttributes);
        dicomRuleAdapter.removeOne(state.dicomRules, id);

        // update matching parent objects
        assertIsDefined(dicomRuleToRemove, 'DICOM rule about to be removed is not defined');
        assertIsDefined(dicomRuleToRemove.modelCustomizationBaseId, 'modelCustomizationBaseId not defined for the DICOM rule about to be removed');
        const customization = state.customizationBases.entities[dicomRuleToRemove.modelCustomizationBaseId];
        assertIsDefined(customization, `Could not find customization base with matching ID ${dicomRuleToRemove.modelCustomizationBaseId}`);
        customizationBaseAdapter.updateOne(state.customizationBases, { id: customization.id, changes: { dicomRules: pull(customization.dicomRules, id), isModified: true } });
        setObjectAndAncestorsAsModified(state, [customization.id], CustomizationObjectType.CustomizationBase);
    },

    [removeDicomAttributeRule.toString()]: (state: StoreState, action: { payload: { id: string } }) => {
        const { id } = action.payload;

        const dicomAttributeToRemove = state.dicomAttributeRules.entities[id];
        if (dicomAttributeToRemove === undefined) { throw new Error(`Could not find DICOM attribute rule ${id} to remove`); }

        const parentDicomRule = state.dicomRules.entities[dicomAttributeToRemove.parentDicomRuleId];
        if (parentDicomRule === undefined) { throw new Error(`Could not find parent DICOM rule ${dicomAttributeToRemove.parentDicomRuleId} for DICOM attribute ${id} -- cannot remove attribute`); }
        dicomRuleAdapter.updateOne(state.dicomRules, {
            id: dicomAttributeToRemove.parentDicomRuleId,
            changes: {
                dicomAttributes: pull(parentDicomRule.dicomAttributes, id),
                isModified: true
            }
        });

        dicomAttributeRuleAdapter.removeOne(state.dicomAttributeRules, id);

        setObjectAndAncestorsAsModified(state, [parentDicomRule.id], CustomizationObjectType.DicomRule);

        validateDicomRuleAndAttributes(state, parentDicomRule.id);
    },

    [revertModelSelectionChanges.toString()]: (state: StoreState, action: { payload: { revertScope: ModelSelectionScope, id?: string } }) => {
        // revert changes to model selection depending on the selected scope
        const { revertScope, id } = action.payload;
        if (revertScope === ModelSelectionScope.All) {
            // revert everything selection-related (except the isModified tag on customization bases and models -- we can't know if those are from selection or
            // customization changes)
            aeTitleRuleAdapter.setAll(state.aeTitleRules, state.originalAeTitleRules.entities as Record<string, AeTitleRule>);
            dicomRuleAdapter.setAll(state.dicomRules, state.originalDicomRules.entities as Record<string, DicomRule>);
            dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, state.originalDicomAttributeRules.entities as Record<string, DicomAttributeRule>);

            // customization base changes are a bit more involved to revert
            // note that removed customization bases are NOT reverted back in -- use 'revert ALL customizations' for that
            // any new customization base selection rules are REMOVED
            customizationBaseAdapter.updateMany(state.customizationBases, state.customizationBases.ids.map(customizationBaseId => {
                const originalCustomizationBase = state.originalCustomizationBases.entities[customizationBaseId];
                return {
                    id: customizationBaseId,
                    changes: {
                        aeTitleRules: originalCustomizationBase?.aeTitleRules || [],
                        dicomRules: originalCustomizationBase?.dicomRules || []
                    }
                };
            }));
        } else if (id !== undefined) {
            // revert specific items
            // TODO: perform some kind of full modification checks here if we also want to mark parent objects as no longer
            // being modified if the reverted entry was the only modified child object (e.g. mark one modelRules entity as 
            // no longer being modified if its only rule was reverted).
            // TODO 2: reverting new items (i.e. ones that do not exist in originalEntities collections) is not supported.
            // Technically we could either a) remove the item (probably weird) or b) set it to empty, default values
            // (also a bit weird)
            if (revertScope === ModelSelectionScope.AeTitleRule) {
                const originalAeTitleRule = state.originalAeTitleRules.entities[id];
                if (originalAeTitleRule === undefined) { throw new Error(`Could not find original AE title rule ${id}`); }
                aeTitleRuleAdapter.setOne(state.aeTitleRules, originalAeTitleRule);
            } else if (revertScope === ModelSelectionScope.DicomRule) {
                const originalDicomRule = state.originalDicomRules.entities[id];
                const currentDicomRule = state.dicomRules.entities[id];
                if (originalDicomRule === undefined || currentDicomRule === undefined) { throw new Error(`Could not find current/original DICOM rule ${id}`); }

                // remove any newly added attribute rules
                dicomAttributeRuleAdapter.removeMany(state.dicomAttributeRules, currentDicomRule.dicomAttributes.filter(da => !originalDicomRule.dicomAttributes.includes(da)));

                // restore original dicom attribute rules
                for (const originalDicomAttributeId of originalDicomRule.dicomAttributes) {
                    const originalDicomAttributeRule = state.originalDicomAttributeRules.entities[originalDicomAttributeId];
                    if (originalDicomAttributeRule === undefined) { throw new Error(`Could not find original DICOM attribute rule ${id}`); }
                    dicomAttributeRuleAdapter.setOne(state.dicomAttributeRules, originalDicomAttributeRule);
                }

                // restore the original dicom rule itself
                dicomRuleAdapter.setOne(state.dicomRules, originalDicomRule);
            } else if (revertScope === ModelSelectionScope.DicomAttributeRule) {
                const originalDicomAttributeRule = state.originalDicomAttributeRules.entities[id];
                if (originalDicomAttributeRule === undefined) { throw new Error(`Could not find original DICOM attribute rule ${id}`); }
                dicomAttributeRuleAdapter.setOne(state.dicomAttributeRules, originalDicomAttributeRule);
            }
        }
    },


    //////////////////////////
    //       UI stuff       //
    //////////////////////////

    [removeModelCustomizationPageFocus.toString()]: (state: StoreState) => {
        state.modelCustomizationPageFocusId = undefined;
    },

    [showFullRoiDetails.toString()]: (state: StoreState, action: { payload: { roiDetails: FullRoiDetailsId } }) => {
        const { roiDetails } = action.payload;
        state.fullRoiDetails = roiDetails;
    },
    [hideFullRoiDetails.toString()]: (state: StoreState) => {
        state.fullRoiDetails = undefined;
    },



    //////////////////////////
    //  Unit test reducers  //
    //////////////////////////
    //
    // these are for unit test use only -- do not use in the regular application!

    [unitTestSetCustomizationObjectPathAsModified.toString()]: (state: StoreState, action: { payload: { objectIds: string[], objectType: CustomizationObjectType } }) => {
        const { objectIds, objectType } = action.payload;
        setObjectAndAncestorsAsModified(state, objectIds, objectType);
    },
    [unitTestAddFormValidationError.toString()]: (state: StoreState, action: { payload: { formError: FormValidationError } }) => {
        formValidationErrorAdapter.addOne(state.formValidationErrors, action.payload.formError);
    },
    [unitTestRemoveFormValidationError.toString()]: (state: StoreState, action: { payload: { formErrorId: string } }) => {
        formValidationErrorAdapter.removeOne(state.formValidationErrors, action.payload.formErrorId);
    },

});






// reused reducer sub-functions:

const updateRoiCustomizationField = (state: StoreState, roiId: string, isGlobalRoi: boolean, changes: Partial<RoiCustomization> | Partial<GlobalRoiCustomization>, noGlobalRoiRecalculate?: boolean) => {
    const roiIds = isGlobalRoi ? state.globalRoiCustomizations.entities[roiId]?.coveredRois : [roiId];

    if (roiIds) {
        // update 1 or more matching rois -- then mark matching models as modified, and also update the global customization entry if applicable
        // TODO: consider calculating here automatically if isModified is needed instead of having to pass it from all the callers
        roiCustomizationAdapter.updateMany(state.roiCustomizations, roiIds.map(roiId => ({ id: roiId, changes: changes })));
        if (!noGlobalRoiRecalculate) {
            if (isGlobalRoi) {
                // if we were modifying a global roi, let's also update that entry to match
                calculateGlobalRoiChangesForGlobalRoi(state, roiId, changes);
            } else {
                // if we're modifying a normal roi, we need to check if this modification removes or adds it from/to a global roi
                calculateGlobalRoiChangesForRegularRoi(state, roiId);
            }
        }
        setObjectAndAncestorsAsModified(state, roiIds, CustomizationObjectType.Roi);
    }
}

const setFmaIdsForRoisInSameCustomizationOutput = (state: StoreState, fmaIdChanges: Partial<FmaId>) => {
    // Ensure that fmaIdChanges is defined
    if (!fmaIdChanges || !fmaIdChanges.modelCustomizationOutputId) {
        throw new Error("Missing FMA ID changes.");
    }

    // Update FMA IDs for ROIs within the specified customization output
    const roisToUpdate = Object.values(state.roiCustomizations.entities).filter(notEmpty).filter(roi => roi.modelCustomizationOutputId === fmaIdChanges.modelCustomizationOutputId);

    roiCustomizationAdapter.updateMany(
        state.roiCustomizations,
        roisToUpdate.map(roi => ({ id: roi.id, changes: { isModified: true, ...fmaIdChanges } }))
    );

    // update parent objects
    setObjectAndAncestorsAsModified(state, [fmaIdChanges.modelCustomizationOutputId], CustomizationObjectType.CustomizationOutput);

    // update global rois
    for (const roi of roisToUpdate) {
        calculateGlobalRoiChangesForRegularRoi(state, roi.id);
    }
};



// 
const setFmaIdsForAllRois = (state: StoreState, fmaIdChanges: Partial<FmaId>) => {
    const modelIds = state.segmentationModels.ids;
    const customizationBasesIds = state.customizationBases.ids;
    const customizationOutputsIds = state.customizationOutputs.ids;

    // Ensure that fmaIdChanges is defined
    if (!fmaIdChanges) {
        throw new Error("Missing FMA ID changes.");
    }

    // Update FMA IDs for all ROIs
    const roisToUpdate = Object.values(state.roiCustomizations.entities).filter(notEmpty);
    const globalRoisToUpdate = Object.values(state.globalRoiCustomizations.entities).filter(notEmpty);

    roiCustomizationAdapter.updateMany(
        state.roiCustomizations,
        roisToUpdate.map(roi => ({ id: roi.id, changes: { isModified: true, ...fmaIdChanges } }))
    );
    globalRoiCustomizationAdapter.updateMany(
        state.globalRoiCustomizations,
        globalRoisToUpdate.map(globalRoi => ({ id: globalRoi.id, changes: { isModified: true, ...fmaIdChanges } }))
    );

    if (modelIds) {
        modelAdapter.updateMany(
            state.segmentationModels,
            modelIds.map(modelId => ({ id: modelId, changes: { isModified: true } }))
        );
    }
    if (customizationBasesIds) {
        customizationBaseAdapter.updateMany(
            state.customizationBases,
            customizationBasesIds.map(customizationBaseId => ({ id: customizationBaseId, changes: { isModified: true } }))
        );
    }
    if (customizationOutputsIds) {
        customizationOutputAdapter.updateMany(
            state.customizationOutputs,
            customizationOutputsIds.map(customizationOutputId => ({ id: customizationOutputId, changes: { isModified: true } }))
        );
    }
};


/** Checks if a just-modified roi customization belongs to a globalRoi, and removes it from it if they are no longer identical,
 * or vice versa. Note that GlobalRoi majority groupings are NOT re-calculated (e.g. if removing this roi from a global roi would
 * make some other group of roi customizations the majority group after this change, the global roi will nevertheless not changed)
 * as that would be confusing during use.
 */
const calculateGlobalRoiChangesForRegularRoi = (state: StoreState, roiId: string) => {
    const roi = state.roiCustomizations.entities[roiId];

    if (roi !== undefined) {
        let isNewGlobalRoiNeeded = false;
        const globalRoiId = roi.globalRoiId;

        if (globalRoiId !== undefined) {
            // check if this roi needs to be removed from matching global roi
            const globalRoi = state.globalRoiCustomizations.entities[globalRoiId];
            if (globalRoi && !roiCustomizationMatchesGlobalRoi(roi, globalRoi)) {
                // we may need to create a new global roi after other operations if operations no longer match
                isNewGlobalRoiNeeded = globalRoi.operation !== roi.operation;

                const coveredRois = globalRoi.coveredRois.filter(rId => rId !== roiId);
                const excludedRois = isNewGlobalRoiNeeded ? globalRoi.excludedRois.filter(r => r !== roiId) :
                    globalRoi.excludedRois.includes(roiId) ? globalRoi.excludedRois : globalRoi.excludedRois.concat(roiId);
                if (!isNewGlobalRoiNeeded) {
                    // update previous global roi out of the roi object, unless we need potentially a completely new global roi
                    // in which case this operation is done later
                    roiCustomizationAdapter.updateOne(state.roiCustomizations, { id: roiId, changes: { globalRoiId: undefined } });
                }

                // either update or remove the previous global roi
                if (coveredRois.length === 0 && excludedRois.length === 0) {
                    globalRoiCustomizationAdapter.removeOne(state.globalRoiCustomizations, globalRoiId);
                } else {
                    globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                        id: globalRoiId,
                        changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, coveredRois, excludedRois) }
                    });
                }
            }
        } else {
            // a new global roi is potentially needed (or the roi can be added to an existing one)
            isNewGlobalRoiNeeded = true;
        }

        if (isNewGlobalRoiNeeded) {
            // check if this roi could be added to an existing global roi
            const globalRoiWithSameOperation = Object.values(state.globalRoiCustomizations.entities).find(g => g?.operation === roi.operation);
            if (globalRoiWithSameOperation !== undefined) {
                // check that global roi and roi are equal
                const matchesGlobalRoi = roiCustomizationMatchesGlobalRoi(roi, globalRoiWithSameOperation);
                const coveredRois = matchesGlobalRoi ?
                    globalRoiWithSameOperation.coveredRois.includes(roiId) ? globalRoiWithSameOperation.coveredRois : globalRoiWithSameOperation.coveredRois.concat(roiId) :
                    globalRoiWithSameOperation.coveredRois.filter(rId => rId !== roiId);
                const excludedRois = !matchesGlobalRoi ?
                    globalRoiWithSameOperation.excludedRois.includes(roiId) ? globalRoiWithSameOperation.excludedRois : globalRoiWithSameOperation.excludedRois.concat(roiId) :
                    globalRoiWithSameOperation.excludedRois.filter(rId => rId !== roiId);
                roiCustomizationAdapter.updateOne(state.roiCustomizations, { id: roiId, changes: { globalRoiId: matchesGlobalRoi ? globalRoiWithSameOperation.id : undefined } });
                globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, {
                    id: globalRoiWithSameOperation.id,
                    changes: { ...getGlobalRoiItemsInAlphabeticalOrder(state, globalRoiWithSameOperation, coveredRois, excludedRois) }
                })
            } else {
                // no existing global roi -- create a new one
                const newGlobalRoi = createGlobalRoiCustomization([roi], [], true);
                roiCustomizationAdapter.updateOne(state.roiCustomizations, { id: roiId, changes: { globalRoiId: newGlobalRoi.id } });
                globalRoiCustomizationAdapter.addOne(state.globalRoiCustomizations, newGlobalRoi);
            }
        }
    }
}

const calculateGlobalRoiChangesForGlobalRoi = (state: StoreState, globalRoiId: string, changes: Partial<GlobalRoiCustomization>) => {
    const globalRoi = state.globalRoiCustomizations.entities[globalRoiId];
    if (globalRoi !== undefined) {
        // apply same updates as were applied for regular rois
        const globalRoiChanges = { ...changes };

        // check if any excluded regular rois now match global roi
        const changedRoiIds: string[] = [];
        for (const roiId of globalRoi.excludedRois) {
            const roi = state.roiCustomizations.entities[roiId];
            if (roi && roiCustomizationMatchesGlobalRoi(roi, globalRoi, changes) && !globalRoi.coveredRois.includes(roiId)) {
                changedRoiIds.push(roiId);
            }
        }

        if (changedRoiIds.length > 0) {
            const coveredChanges = globalRoi.coveredRois.concat(changedRoiIds);
            const excludedChanges = globalRoi.excludedRois.filter(rId => !changedRoiIds.includes(rId));
            const sortedChanges = getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, coveredChanges, excludedChanges);
            globalRoiChanges['coveredRois'] = sortedChanges.coveredRois;
            globalRoiChanges['excludedRois'] = sortedChanges.excludedRois;
            roiCustomizationAdapter.updateMany(state.roiCustomizations, changedRoiIds.map(rId => ({ id: rId, changes: { globalRoiId: globalRoiId } })));
        }


        globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, { id: globalRoiId, changes: { isModified: true, ...globalRoiChanges } });
    }
}

const ensureGlobalRoiItemsAreInAlphabeticalOrder = (state: StoreState, globalRoiId: string) => {
    const globalRoi = state.globalRoiCustomizations.entities[globalRoiId];
    if (globalRoi === undefined) {
        throw new Error(`Could not retrieve global roi ${globalRoiId}`);
    }

    const changes = getGlobalRoiItemsInAlphabeticalOrder(state, globalRoi, globalRoi.coveredRois, globalRoi.excludedRois);

    globalRoiCustomizationAdapter.updateOne(state.globalRoiCustomizations, { id: globalRoiId, changes: { ...changes } });
}

const getGlobalRoiItemsInAlphabeticalOrder = (state: StoreState, globalRoi: GlobalRoiCustomization, coveredRoiIds: string[], excludedRoiIds: string[])
    : { coveredRois: string[], excludedRois: string[] } => {
    const coveredRois = coveredRoiIds.map(cId => state.roiCustomizations.entities[cId]);
    if (coveredRois.some(c => c === undefined || c.modelCustomizationOutputId === undefined)) { throw new Error(`Global roi ${globalRoi.id} covered ROIs list contains one or more undefined rois`); }
    const excludedRois = excludedRoiIds.map(cId => state.roiCustomizations.entities[cId]);
    if (excludedRois.some(c => c === undefined || c.modelCustomizationOutputId === undefined)) { throw new Error(`Global roi ${globalRoi.id} excludedRois ROIs list contains one or more undefined rois`); }

    const coveredRoisWithOutputs = (coveredRois as RoiCustomization[]).map(c => ({ roiId: c.id, output: state.customizationOutputs.entities[c.modelCustomizationOutputId!] }));
    if (coveredRoisWithOutputs.some(c => c.output === undefined)) { throw new Error(`No output found for a covered roi in ${globalRoi.id}`); }
    const coveredRoisWithOutputsAndBases = (coveredRoisWithOutputs).map(c => ({ roiId: c.roiId, output: c.output!, base: state.customizationBases.entities[c.output!.modelCustomizationBaseId] }));
    if (coveredRoisWithOutputsAndBases.some(c => c.base === undefined)) { throw new Error(`No base found for a covered roi in ${globalRoi.id}`); }
    const coveredRoisWithOutputsAndBasesAndModels = (coveredRoisWithOutputsAndBases).map(c => ({ roiId: c.roiId, output: c.output!, base: c.base!, model: state.segmentationModels.entities[c.base!.segmentationModelId] }));
    if (coveredRoisWithOutputsAndBasesAndModels.some(c => c.model === undefined)) { throw new Error(`No model found for a covered roi in ${globalRoi.id}`); }
    const toBeSortedCoveredRois = coveredRoisWithOutputsAndBasesAndModels.map(c => ({ roiId: c.roiId, label: `${c.model!.modelName}.${c.base.customizationName}.${c.output.filename}` }));

    const excludedRoisWithOutputs = (excludedRois as RoiCustomization[]).map(c => ({ roiId: c.id, output: state.customizationOutputs.entities[c.modelCustomizationOutputId!] }));
    if (excludedRoisWithOutputs.some(c => c.output === undefined)) { throw new Error(`No output found for an excluded roi in ${globalRoi.id}`); }
    const excludedRoisWithOutputsAndBases = (excludedRoisWithOutputs).map(c => ({ roiId: c.roiId, output: c.output!, base: state.customizationBases.entities[c.output!.modelCustomizationBaseId] }));
    if (excludedRoisWithOutputsAndBases.some(c => c.base === undefined)) { throw new Error(`No base found for an excluded roi in ${globalRoi.id}`); }
    const excludedRoisWithOutputsAndBasesAndModels = (excludedRoisWithOutputsAndBases).map(c => ({ roiId: c.roiId, output: c.output!, base: c.base!, model: state.segmentationModels.entities[c.base!.segmentationModelId] }));
    if (excludedRoisWithOutputsAndBasesAndModels.some(c => c.model === undefined)) { throw new Error(`No model found for an excluded roi in ${globalRoi.id}`); }
    const toBeSortedExcludedRois = excludedRoisWithOutputsAndBasesAndModels.map(c => ({ roiId: c.roiId, label: `${c.model!.modelName}.${c.base.customizationName}.${c.output.filename}` }));

    const sortedCoveredRoiIds = naturalSort(toBeSortedCoveredRois, 'label').map(c => c.roiId);
    const sortedExcludedRoiIds = naturalSort(toBeSortedExcludedRois, 'label').map(c => c.roiId);

    return { coveredRois: sortedCoveredRoiIds, excludedRois: sortedExcludedRoiIds };
}

type DuplicatedIdMap = { sourceId: string, targetId: string };

type DuplicatedOutputEntities = {
    duplicatedOutput: ModelCustomizationOutput;
    newRois: RoiCustomization[];
    newMetadata: ModelCustomizationMetadataItem[];
    globalRoiChanges: Update<GlobalRoiCustomization>[];
    duplicatedIds: DuplicatedIdMap[];
};

const duplicateCustomizationOutputFromStore = (state: StoreState, sourceCustomizationOutput: ModelCustomizationOutput,
    newFilename?: string, newCustomizationBaseId?: string): DuplicatedOutputEntities => {

    const newRois: RoiCustomization[] = [];
    const newMetadata: ModelCustomizationMetadataItem[] = [];
    const globalRoiChanges: Update<GlobalRoiCustomization>[] = [];
    const newCustomizationOutputId = generateNewId();
    const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: sourceCustomizationOutput.id, targetId: newCustomizationOutputId }];

    let duplicateFilename: string = '';

    // duplicate metadata
    for (const metadataId of sourceCustomizationOutput.metadata) {
        const metadata = state.modelCustomizationsMetadata.entities[metadataId];
        if (!metadata) { throw new Error(`Could not find customization metadata with id ${metadataId}`) }
        const duplicatedMetadata = duplicateModelCustomizationMetadataItem(metadata, newCustomizationOutputId);

        // handle filename metadata
        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE && newFilename !== undefined) {
            duplicatedMetadata.value = ensureFilenameHasDcmExtension(newFilename);
        }

        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE) {
            duplicateFilename = duplicatedMetadata.value;
        }

        newMetadata.push(duplicatedMetadata);
        duplicatedIds.push({ sourceId: metadataId, targetId: duplicatedMetadata.id });
    }

    // duplicate rois
    for (const roiId of sourceCustomizationOutput.rois) {
        const roi = state.roiCustomizations.entities[roiId];
        if (!roi) { throw new Error(`Could not find roi with id ${roiId}`); }
        const duplicatedRoi = duplicateRoiCustomization(roi, newCustomizationOutputId);
        newRois.push(duplicatedRoi);
        duplicatedIds.push({ sourceId: roiId, targetId: duplicatedRoi.id });
    }

    // create new output
    const roiIds = newRois.map(r => r.id);
    const metadataIds = newMetadata.map(m => m.id);
    const duplicatedOutput = createNewModelCustomizationOutput(metadataIds, roiIds, newCustomizationBaseId || sourceCustomizationOutput.modelCustomizationBaseId, duplicateFilename, newCustomizationOutputId, true);

    // update global rois -- don't recalculate globals
    for (const newRoi of newRois) {
        const globalRoiId = newRoi.globalRoiId;
        if (globalRoiId) {
            // if original roi matched a global roi, then the duplicate should also
            const globalRoi = state.globalRoiCustomizations.entities[globalRoiId];
            if (!globalRoi) { throw new Error(`Could not find global roi with id ${globalRoiId}`); }
            globalRoiChanges.push({ id: globalRoiId, changes: { coveredRois: globalRoi.coveredRois.concat(newRoi.id) } });
        } else {
            // if the original roi did not match a global roi, then find the matching entry and put the duplicate also into excluded rois
            const globalRoi = Object.values(state.globalRoiCustomizations.entities).find(g => g?.operation === newRoi.operation);
            if (globalRoi) {
                globalRoiChanges.push({ id: globalRoi.id, changes: { excludedRois: globalRoi.excludedRois.concat(newRoi.id) } });
            }
        }
    }

    return {
        duplicatedOutput,
        newMetadata,
        newRois,
        globalRoiChanges,
        duplicatedIds,
    };
};

type RemovedOutputEntities = {
    globalRoiChanges: Update<GlobalRoiCustomization>[];
    roiIdsToBeRemoved: string[];
    metadataIdsToBeRemoved: string[];
};

/** Collects which store entities need to be removed and updated when a customization output is removed. */
const removeCustomizationOutputFromStore = (state: StoreState, output: ModelCustomizationOutput | undefined, customizationBaseId: string): RemovedOutputEntities => {

    const globalRoiChanges: Update<GlobalRoiCustomization>[] = [];
    const roiIdsToBeRemoved: string[] = [];
    const metadataIdsToBeRemoved: string[] = [];

    // update global rois before the actual rois are removed
    if (output === undefined) {
        throw new Error(`An invalid customization output was retrieved for customization base ${customizationBaseId}`);
    }

    metadataIdsToBeRemoved.push(...output.metadata);

    for (const roiId of output.rois) {
        const roi = state.roiCustomizations.entities[roiId];
        if (roi === undefined) {
            throw new Error(`Could not retrieve roi ${roiId} for output ${output.id} in customization base ${customizationBaseId}`);
        }

        roiIdsToBeRemoved.push(roiId);

        const globalRoi = roi.globalRoiId ?
            state.globalRoiCustomizations.entities[roi.globalRoiId] :
            Object.values(state.globalRoiCustomizations.entities).find(r => r?.excludedRois.includes(roiId));
        if (globalRoi) {
            globalRoiChanges.push({
                id: globalRoi.id, changes: {
                    excludedRois: globalRoi.excludedRois.filter(r => r !== roiId),
                    coveredRois: globalRoi.coveredRois.filter(r => r !== roiId),
                }
            });
        }
    }

    return {
        globalRoiChanges,
        roiIdsToBeRemoved,
        metadataIdsToBeRemoved
    };
};

/** Returns all store IDs for FormValidationError objects matching given itemId (e.g. roiId) */
const getFormValidationErrorIdsForItem = (state: StoreState, itemId: string): string[] => {
    const validationErrors = Object.values(state.formValidationErrors.entities).filter(e => e?.itemId === itemId).filter(notEmpty);
    return validationErrors.map(e => e.id);
}

/** Returns all store IDs for FormValidationError objects matching given itemIds (e.g. roiId) */
const getFormValidationErrorIdsForItems = (state: StoreState, itemIds: string[]): string[] => {
    const validationErrors = Object.values(state.formValidationErrors.entities).filter(notEmpty).filter(e => itemIds.includes(e.itemId));
    return validationErrors.map(e => e.id);
}

/** Duplicate FormValidationErrors for given itemIds */
const duplicateFormValidationErrors = (state: StoreState, duplicatedIds: DuplicatedIdMap[]): FormValidationError[] => {

    const sourceIds = duplicatedIds.map(d => d.sourceId);
    const errorsToDuplicate = Object.values(state.formValidationErrors.entities).filter(notEmpty).filter(e => sourceIds.includes(e.itemId));

    const duplicatedErrors: FormValidationError[] = [];
    duplicatedErrors.push(...errorsToDuplicate.map(e => duplicateFormValidationError(e, duplicatedIds.find(d => d.sourceId === e.itemId)!.targetId)));

    return duplicatedErrors;
}

type FormValidationErrorCalculationResults = { validationErrorIdsToRemove: string[], validationErrorsToAdd: FormValidationError[] };

/** Calculates form validation errors for given customization output.
 * TODO: once we have more types of validation errors consider splitting
 * this into smaller functions, and consider whether all of them need to be
 * checked here.
 */
const calculateFormValidationErrorsForCustomizationOutput = (state: StoreState, outputId: string): FormValidationErrorCalculationResults => {
    const errorIdsToRemove: string[] = [];
    const errorsToAdd: FormValidationError[] = [];

    const output = state.customizationOutputs.entities[outputId];
    if (!output) { throw new Error(`Customization output (${outputId}) is undefined`); }
    const rois = output.rois.map(rId => state.roiCustomizations.entities[rId]).filter(notEmpty);

    const existingErrors = Object.values(state.formValidationErrors.entities).filter(notEmpty).filter(e => output.rois.includes(e?.itemId));
    const existingEmptyNameErrors = existingErrors.filter(e => e?.errorType === FormValidationErrorType.RoiNameIsEmpty);
    const existingDuplicateNameErrors = existingErrors.filter(e => e?.errorType === FormValidationErrorType.DuplicateRoiNamesInOutput);

    // collect existing duplicate name errors nearby so we can use them when doing a delta within this output
    // map potential INCLUDED duplicate roiIds (string array value) under the potential duplicate name (string key)
    // this is needed for the duplicate name check resolution later
    const duplicateNames: { [name: string]: string[] } = {};
    for (const roi of rois) {
        if (roi.isIncluded) {
            duplicateNames[roi.name] = duplicateNames[roi.name] || [];
            duplicateNames[roi.name].push(roi.id);
        }
    }

    // check all the validation errors for ROIs in one loop
    for (const roi of rois) {

        let dontAddNewValidationErrors = false;


        // check for empty names rois (RoiNameIsEmpty)
        const matchingExistingEmptyNameErrors = existingEmptyNameErrors.filter(e => e.itemId === roi.id);
        const hasMatchingEmptyNameErrors = matchingExistingEmptyNameErrors.length > 0;
        const isNameEmpty = !roi.name.trim();

        // NOTE: new errors are NOT created if existing ones with the same FormValidationErrorType have already been found!
        if (isNameEmpty && !hasMatchingEmptyNameErrors && !dontAddNewValidationErrors) {
            // create a new error
            errorsToAdd.push(generateNewFormValidationError(FormValidationErrorType.RoiNameIsEmpty, roi.id, CustomizationObjectType.Roi));
        } else if (dontAddNewValidationErrors || (!isNameEmpty && hasMatchingEmptyNameErrors)) {
            // remove existing errors
            errorIdsToRemove.push(...matchingExistingEmptyNameErrors.map(m => m.id));
        }

        // if this name was empty then don't add any new validation errors (but we still need to check if any can be removed)
        if (isNameEmpty) {
            dontAddNewValidationErrors = true;
        }


        // check for duplicate names within (included) rois in the same output (DuplicateRoiNamesInOutput)
        // now loop through the ROIs again and do a delta against existing errors, creating new errors and marking existing ones to be removed as needed
        const matchingExistingDuplicateNameErrors = existingDuplicateNameErrors.filter(e => e.itemId === roi.id);
        const hasMatchingDuplicateNameErrors = matchingExistingDuplicateNameErrors.length > 0;
        const isNameDuplicated = duplicateNames[roi.name] && duplicateNames[roi.name].length > 1;

        // NOTE: new errors are NOT created if existing ones with the same FormValidationErrorType have already been found!
        if (isNameDuplicated && !hasMatchingDuplicateNameErrors && !dontAddNewValidationErrors) {
            // create a new error
            errorsToAdd.push(generateNewFormValidationError(FormValidationErrorType.DuplicateRoiNamesInOutput, roi.id, CustomizationObjectType.Roi));
        } else if (dontAddNewValidationErrors || (!isNameDuplicated && hasMatchingDuplicateNameErrors)) {
            // remove existing errors
            errorIdsToRemove.push(...matchingExistingDuplicateNameErrors.map(m => m.id));
        }
    }

    return {
        validationErrorIdsToRemove: errorIdsToRemove,
        validationErrorsToAdd: errorsToAdd,
    }
}


// TODO: Currently unused, but may be helpful in a future case where you want to tie customization type guard
// functionality under CustomizationObjectType enum value.
// /** A collection of type guard functions for certain customization object types (not all are supported) */
// const getCustomizationTypeGuard: { [type in CustomizationObjectType]: (obj: any) => obj is AnyCustomizationType } = {
//     [CustomizationObjectType.None]: function (obj: any): obj is AnyCustomizationType {
//         throw new Error('Unsupported customization object type for this function.');
//     },
//     [CustomizationObjectType.Model]: isSegmentationModel,
//     [CustomizationObjectType.CustomizationBase]: isModelCustomizationBase,
//     [CustomizationObjectType.CustomizationOutput]: isModelCustomizationOutput,
//     [CustomizationObjectType.Metadata]: isModelCustomizationMetadata,
//     [CustomizationObjectType.Roi]: isRoiCustomization,
//     [CustomizationObjectType.AeTitleRule]: isAeTitleRule,
//     [CustomizationObjectType.DicomRule]: isDicomRule,
//     [CustomizationObjectType.DicomAttributeRule]: isDicomAttributeRule,
//     [CustomizationObjectType.GlobalRoi]: function (obj: any): obj is AnyCustomizationType {
//         throw new Error('Unsupported customization object type for this function.');
//     },
//     [CustomizationObjectType.TriggerRule]: function (obj: any): obj is AnyCustomizationType {
//         throw new Error('Unsupported customization object type for this function.');
//     },
//     [CustomizationObjectType.FmaId]: function (obj: any): obj is AnyCustomizationType {
//         throw new Error('Unsupported customization object type for this function.');
//     },
//     [CustomizationObjectType.PhysicalProperties]: function (obj: any): obj is AnyCustomizationType {
//         throw new Error('Unsupported customization object type for this function.');
//     }
// }

/** Find and returns the parent object for a segmentation model customization object, if it's in redux store. Throws otherwise. */
const getParent = (state: StoreState, obj: AnyCustomizationType): AnyCustomizationType => {
    if (isSegmentationModel(obj)) {
        throw new Error('Segmentation models thenselves have no parent objects!');
    }

    if (isModelCustomizationBase(obj)) {
        const model = Object.values(state.segmentationModels.entities).find(m => m?.customizations.includes(obj.id));
        doAssert(model, 'model');
        return model;
    }

    if (isModelCustomizationOutput(obj)) {
        const base = Object.values(state.customizationBases.entities).find(c => c?.outputs.includes(obj.id));
        doAssert(base, 'customization base');
        return base;
    }

    if (isModelCustomizationMetadata(obj)) {
        const output = obj.modelCustomizationOutputId ?
            state.customizationOutputs.entities[obj.modelCustomizationOutputId] :
            Object.values(state.customizationOutputs.entities).find(o => o?.metadata.includes(obj.id));
        doAssert(output, 'customization output');
        return output;
    }

    if (isRoiCustomization(obj)) {
        const output = obj.modelCustomizationOutputId ?
            state.customizationOutputs.entities[obj.modelCustomizationOutputId] :
            Object.values(state.customizationOutputs.entities).find(o => o?.metadata.includes(obj.id));
        doAssert(output, 'customization output');
        return output;
    }

    if (isAeTitleRule(obj)) {
        const base = obj.modelCustomizationBaseId ?
            state.customizationBases.entities[obj.modelCustomizationBaseId] :
            Object.values(state.customizationBases.entities).find(c => c?.aeTitleRules.includes(obj.id));
        doAssert(base, 'customization base');
        return base;
    }

    if (isDicomRule(obj)) {
        const base = obj.modelCustomizationBaseId ?
            state.customizationBases.entities[obj.modelCustomizationBaseId] :
            Object.values(state.customizationBases.entities).find(c => c?.dicomRules.includes(obj.id));
        doAssert(base, 'customization base');
        return base;
    }

    if (isDicomAttributeRule(obj)) {
        const dicomRule = state.dicomRules.entities[obj.parentDicomRuleId];
        doAssert(dicomRule, 'DICOM rule');
        return dicomRule;
    }

    throw new Error('Unsupported type');
};

function doAssert<T>(obj: T, typeName: string, id?: string): asserts obj is NonNullable<T> {
    if (obj === undefined || obj === null) { throw new Error(`Could not find a valid ${typeName} object${id ? ` with given ID '${id}'` : ''}`); }
};

/** Marks the given customization view model object and all its parent objects as 'modified'. */
const setObjectAndAncestorsAsModified = (state: StoreState, objectIds: string[], objectType: CustomizationObjectType) => {

    // these IDs will all be marked as modified in one go
    const targets: { [parentType in CustomizationObjectType]: string[] } = {
        [CustomizationObjectType.None]: [],
        [CustomizationObjectType.Model]: [],
        [CustomizationObjectType.CustomizationBase]: [],
        [CustomizationObjectType.CustomizationOutput]: [],
        [CustomizationObjectType.Metadata]: [],
        [CustomizationObjectType.Roi]: [],
        [CustomizationObjectType.GlobalRoi]: [],
        [CustomizationObjectType.AeTitleRule]: [],
        [CustomizationObjectType.DicomRule]: [],
        [CustomizationObjectType.DicomAttributeRule]: [],
        [CustomizationObjectType.TriggerRule]: [],
        [CustomizationObjectType.FmaId]: [],
        [CustomizationObjectType.PhysicalProperties]: [],
    };

    // walk through the entire hierarchy in reverse order & set everything touched as modified

    // set initial child objects
    targets[objectType] = objectIds;

    for (const id of targets[CustomizationObjectType.DicomAttributeRule]) {
        const dicomAttributeRule = state.dicomAttributeRules.entities[id];
        doAssert(dicomAttributeRule, 'DICOM attribute rule', id);
        targets[CustomizationObjectType.DicomRule].push(getParent(state, dicomAttributeRule).id);
    }

    for (const id of targets[CustomizationObjectType.DicomRule]) {
        const dicomRule = state.dicomRules.entities[id];
        doAssert(dicomRule, 'DICOM rule', id);
        targets[CustomizationObjectType.CustomizationBase].push(getParent(state, dicomRule).id);
    }

    for (const id of targets[CustomizationObjectType.AeTitleRule]) {
        const aeTitleRule = state.aeTitleRules.entities[id];
        doAssert(aeTitleRule, 'AE title rule rule', id);
        targets[CustomizationObjectType.CustomizationBase].push(getParent(state, aeTitleRule).id);
    }

    for (const id of targets[CustomizationObjectType.GlobalRoi]) {
        const globalRoi = state.globalRoiCustomizations.entities[id];
        doAssert(globalRoi, 'global ROI customization', id);
        // global roi special case: add all covered regular roi items
        targets[CustomizationObjectType.Roi].push(...globalRoi.coveredRois);
    }

    for (const id of targets[CustomizationObjectType.Roi]) {
        const roi = state.roiCustomizations.entities[id];
        doAssert(roi, 'ROI customization', id);
        targets[CustomizationObjectType.CustomizationOutput].push(getParent(state, roi).id);
    }

    for (const id of targets[CustomizationObjectType.Metadata]) {
        const metadata = state.modelCustomizationsMetadata.entities[id];
        doAssert(metadata, 'customization metadata', id);
        targets[CustomizationObjectType.CustomizationOutput].push(getParent(state, metadata).id);
    }

    for (const id of targets[CustomizationObjectType.CustomizationOutput]) {
        const output = state.customizationOutputs.entities[id];
        doAssert(output, 'customization output', id);
        targets[CustomizationObjectType.CustomizationBase].push(getParent(state, output).id);
    }

    for (const id of targets[CustomizationObjectType.CustomizationBase]) {
        const customization = state.customizationBases.entities[id];
        doAssert(customization, 'customization base', id);
        targets[CustomizationObjectType.Model].push(getParent(state, customization).id);
    }


    // apply updates

    if (targets[CustomizationObjectType.Model].length > 0) {
        modelAdapter.updateMany(state.segmentationModels, targets[CustomizationObjectType.Model].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.CustomizationBase].length > 0) {
        customizationBaseAdapter.updateMany(state.customizationBases, targets[CustomizationObjectType.CustomizationBase].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.CustomizationOutput].length > 0) {
        customizationOutputAdapter.updateMany(state.customizationOutputs, targets[CustomizationObjectType.CustomizationOutput].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.Metadata].length > 0) {
        modelCustomizationsMetadataAdapter.updateMany(state.modelCustomizationsMetadata, targets[CustomizationObjectType.Metadata].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.Roi].length > 0) {
        roiCustomizationAdapter.updateMany(state.roiCustomizations, targets[CustomizationObjectType.Roi].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.GlobalRoi].length > 0) {
        globalRoiCustomizationAdapter.updateMany(state.globalRoiCustomizations, targets[CustomizationObjectType.GlobalRoi].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.AeTitleRule].length > 0) {
        aeTitleRuleAdapter.updateMany(state.aeTitleRules, targets[CustomizationObjectType.AeTitleRule].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.DicomRule].length > 0) {
        dicomRuleAdapter.updateMany(state.dicomRules, targets[CustomizationObjectType.DicomRule].map(id => ({ id, changes: { isModified: true } })));
    }

    if (targets[CustomizationObjectType.DicomAttributeRule].length > 0) {
        dicomAttributeRuleAdapter.updateMany(state.dicomAttributeRules, targets[CustomizationObjectType.DicomAttributeRule].map(id => ({ id, changes: { isModified: true } })));
    }
}


/** Perform form validation for given customization output and apply results to state. */
const performFormValidationForOutput = (state: StoreState, outputId: string) => {
    const results = calculateFormValidationErrorsForCustomizationOutput(state, outputId);

    // add new errors
    if (results.validationErrorsToAdd.length > 0) {
        formValidationErrorAdapter.addMany(state.formValidationErrors, results.validationErrorsToAdd);
    }

    // remove outdated errors
    if (results.validationErrorIdsToRemove.length > 0) {
        formValidationErrorAdapter.removeMany(state.formValidationErrors, results.validationErrorIdsToRemove);
    }
}


const validateAeTitleRuleActionUniqueness = (state: StoreState, action: string) => {
    const aeTitleRulesWithSameAction = Object.values(state.aeTitleRules.entities).filter(ae => ae?.action === action);
    // values in this array should actually never be undefined but EntityState collections are being uncooperative here
    if (aeTitleRulesWithSameAction.length > 1) {
        aeTitleRulesWithSameAction.forEach(ae => {
            if (ae !== undefined) {
                ae.isValid = false;
                ae.validationMessage = VALIDATION_MESSAGE_AE_TITLES_MUST_BE_UNIQUE;
            }
        });
    } else if (aeTitleRulesWithSameAction[0] !== undefined) {
        aeTitleRulesWithSameAction[0].isValid = true;
        aeTitleRulesWithSameAction[0].validationMessage = undefined;
    }
}


/** Perform full validation on DICOM rule and related DICOM attribute rules. Set validation states to the store objects accordingly. */
const validateDicomRuleAndAttributes = (state: StoreState, dicomRuleId: string): boolean => {

    const dicomRule = state.dicomRules.entities[dicomRuleId];

    if (!dicomRule) { return true; }

    // collect dicom attributes for this rule
    const dicomAttributeRules = dicomRule.dicomAttributes.map(aId => state.dicomAttributeRules.entities[aId]).filter(notEmpty);

    // do the validation and collect passed and failed entity IDs:

    // contains dicom rule ids
    const hasAtLeastOneAttributeResults = validateDicomRuleHasAtLeastOneAttribute(dicomRule);

    // these two contain dicom ATTRIBUTE ids
    const attributeNameNotEmptyAttributeRules = validateDicomAttributeNamesAreNotEmpty(dicomAttributeRules);
    const attributeNameNotUniqueAttributeRules = validateAllDicomAttributeNamesHaveNoDuplicates(dicomAttributeRules);
    const allFailedDicomAttributeRules = attributeNameNotEmptyAttributeRules.failedIds.concat(attributeNameNotUniqueAttributeRules.failedIds);

    const isValidOverall = (hasAtLeastOneAttributeResults.failedIds.length + allFailedDicomAttributeRules.length) === 0;

    // collect existing validation errors for delta
    const existingErrors = Object.values(state.formValidationErrors.entities)
        .filter(notEmpty)
        .filter(e => [
            FormValidationErrorType.DicomRuleMustHaveAtLeastOneAttribute,
            FormValidationErrorType.DicomAttributeNameMustNotBeEmpty,
            FormValidationErrorType.DicomAttributesMustNotRepeat
        ].includes(e.errorType));

    // handle dicom rule validation error (if any)
    // NOTE: this bit needs to be rewritten once there are more than one validation rules for dicom rules themselves!
    const dicomRulePassed = hasAtLeastOneAttributeResults.failedIds.length === 0;
    const existingDicomRuleError = existingErrors.find(e => e.errorType === FormValidationErrorType.DicomRuleMustHaveAtLeastOneAttribute && e.itemId === dicomRuleId);
    if (dicomRulePassed && existingDicomRuleError !== undefined) {
        // remove existing error
        formValidationErrorAdapter.removeOne(state.formValidationErrors, existingDicomRuleError.id);
    } else if (!dicomRulePassed && !existingDicomRuleError) {
        // add a new error
        formValidationErrorAdapter.addOne(
            state.formValidationErrors,
            generateNewFormValidationError(FormValidationErrorType.DicomRuleMustHaveAtLeastOneAttribute, dicomRuleId, CustomizationObjectType.DicomRule));
    }

    // handle dicom attribute errors
    const existingDicomAttributeErrors = existingErrors
        .filter(e => e.errorType !== FormValidationErrorType.DicomRuleMustHaveAtLeastOneAttribute
            && dicomRule.dicomAttributes.includes(e.itemId));
    for (const dicomAttributeId of dicomRule.dicomAttributes) {
        // we need to collect old validation errors that we need to remove
        const existingErrorIdsToRemove: string[] = [];
        const existingErrorsForThisAttribute = existingDicomAttributeErrors.filter(e => e.itemId === dicomAttributeId);
        const existingEmptyNameErrors = existingErrorsForThisAttribute.filter(e => e.errorType === FormValidationErrorType.DicomAttributeNameMustNotBeEmpty);
        const existingDuplicateNamesErrors = existingErrorsForThisAttribute.filter(e => e.errorType === FormValidationErrorType.DicomAttributesMustNotRepeat);

        // empty name error overrides duplicate name error
        if (attributeNameNotEmptyAttributeRules.failedIds.includes(dicomAttributeId)) {
            existingErrorIdsToRemove.push(...existingDuplicateNamesErrors.map(e => e.id));
            if (existingEmptyNameErrors.length === 0) {
                formValidationErrorAdapter.addOne(state.formValidationErrors, generateNewFormValidationError(
                    FormValidationErrorType.DicomAttributeNameMustNotBeEmpty, dicomAttributeId, CustomizationObjectType.DicomAttributeRule
                ));
            }
        } else if (attributeNameNotUniqueAttributeRules.failedIds.includes(dicomAttributeId)) {
            existingErrorIdsToRemove.push(...existingEmptyNameErrors.map(e => e.id));
            if (existingDuplicateNamesErrors.length === 0) {
                formValidationErrorAdapter.addOne(state.formValidationErrors, generateNewFormValidationError(
                    FormValidationErrorType.DicomAttributesMustNotRepeat, dicomAttributeId, CustomizationObjectType.DicomAttributeRule
                ));
            }
        } else {
            // this attribute passed validation
            existingErrorIdsToRemove.push(...(existingEmptyNameErrors.map(e => e.id).concat(existingDuplicateNamesErrors.map(e => e.id))));
        }

        // remove any existing validation errors tagged as such
        if (existingErrorIdsToRemove.length > 0) {
            formValidationErrorAdapter.removeMany(state.formValidationErrors, existingErrorIdsToRemove);
        }
    }

    // update isValid props
    dicomRuleAdapter.updateOne(state.dicomRules, { id: dicomRuleId, changes: { isValid: isValidOverall } });
    dicomAttributeRuleAdapter.updateMany(state.dicomAttributeRules, dicomRule.dicomAttributes.map(id => ({ id: id, changes: { isValid: !allFailedDicomAttributeRules.includes(id) } })));

    return isValidOverall;
}

type DicomAttributeValidationResults = { passedIds: string[]; failedIds: string[]; };

/** Checks that given dicom rule has at least one attribute. Returns a list of item ids which fail and which pass the validation. */
const validateDicomRuleHasAtLeastOneAttribute = (dicomRule: DicomRule): DicomAttributeValidationResults => {
    const validationResults: DicomAttributeValidationResults = { passedIds: [], failedIds: [] };

    const dicomRuleId = dicomRule.id;

    const dicomAttributeRuleIds = dicomRule.dicomAttributes;
    if (dicomAttributeRuleIds.length < 1) {
        validationResults.failedIds.push(dicomRuleId);
    } else {
        validationResults.passedIds.push(dicomRuleId);
    }

    return validationResults;
}

/** Checks that given dicom attribute's name is not empty. Returns a list of item ids which fail and which pass the validation. */
const validateDicomAttributeNamesAreNotEmpty = (dicomAttributeRules: DicomAttributeRule[]): DicomAttributeValidationResults => {
    const validationResults: DicomAttributeValidationResults = { passedIds: [], failedIds: [] };

    // this must be done for every dicom attribute rule in the dicom rule in one go,
    // not only just the one we just edited. otherwise we will have cases where other
    // validation rules will erroneously supercede this validation case when a dicom
    // attribute is being removed
    for (const attributeRule of dicomAttributeRules) {
        if (attributeRule.attribute !== undefined && attributeRule.attribute.trim() === '') {
            validationResults.failedIds.push(attributeRule.id);
        } else {
            validationResults.passedIds.push(attributeRule.id);
        }
    }

    return validationResults;
}

/** Validates that the dicom attribute specified in a dicom attribute rule is unique within its whole dicom rule
 * (i.e. no duplicates). Returns a list of item ids which fail and which pass the validation.
 */
const validateAllDicomAttributeNamesHaveNoDuplicates = (dicomAttributeRules: DicomAttributeRule[]): DicomAttributeValidationResults => {
    const validationResults: DicomAttributeValidationResults = { passedIds: [], failedIds: [] };

    // first, collect all dicom attribute names and store their IDs
    const duplicateNames: { [attributeName: string]: string[] } = {};
    for (const attributeRule of dicomAttributeRules) {
        duplicateNames[attributeRule.attribute] = duplicateNames[attributeRule.attribute] || [];
        duplicateNames[attributeRule.attribute].push(attributeRule.id);
    }

    // then, invalidate all rules that share attribute names, and validate everything else
    for (const attributeName of Object.keys(duplicateNames)) {
        if (duplicateNames[attributeName].length > 1) {
            // set all dicom attribute IDs as invalid
            validationResults.failedIds.push(...duplicateNames[attributeName]);
        } else {
            // set as valid
            validationResults.passedIds.push(duplicateNames[attributeName][0]);
        }
    }

    return validationResults;
}

const convertCustomizationValidationErrorToStoreObjects = (state: StoreState, error: BackendValidationError): CustomizationValidationError[] => {
    const { json } = error;

    if (json === undefined) {
        throw new Error('Could not retrieve proper error messages as given JSON is undefined');
    }

    const jsonData = JSON.parse(json);
    const storeErrors: CustomizationValidationError[] = [];

    error.validationErrors.forEach(e => {
        let cursor = jsonData;
        let cursorPath = '';
        let targetType = CustomizationObjectType.None;
        let target: { id: string, type: CustomizationObjectType } | undefined = undefined;
        for (const locValue of e.loc) {
            if (locValue === LOC_VALUE_BODY) {
                cursor = jsonData;
                cursorPath = 'body';
            } else if (targetType === CustomizationObjectType.FmaId) {
                // special case handling for fma id entries
                break;
            }
            else {
                cursor = cursor[locValue];
                cursorPath += `/${locValue}`;
                if (cursor === undefined) {
                    if (target && target.type === CustomizationObjectType.AeTitleRule && locValue === 'action') {
                        // special case for ae titles where we might have two 'action' nodes
                        // sequentially -- in this case just let the code fall through
                    } else {
                        console.error(target)
                        throw new Error(`Could not find JSON node matching error message loc: ${cursorPath}`);
                    }
                }
            }

            // if locValue is a string, we need to figure out what the next type of object will be
            if (isString(locValue)) {
                targetType = getTargetType(locValue);
            }

            // if locValue is a number, we need to figure out which exact object of target type we're dealing with
            else if (isNumber(locValue)) {
                target = getTargetObject(state, targetType, cursor, target, cursorPath);
            }
        }

        if (target) {

            // replace dicom attributes with their parent dicom rules for now
            if (target.type === CustomizationObjectType.DicomAttributeRule) {
                const dicomAttribute = state.dicomAttributeRules.entities[target.id];
                if (dicomAttribute) {
                    target.id = dicomAttribute.parentDicomRuleId;
                    target.type = CustomizationObjectType.DicomRule;
                }
            }

            const field = e.loc && e.loc.length > 0 ? e.loc[e.loc.length - 1] as string : undefined;

            const validationError = {
                id: target.id,
                type: target.type,
                message: e.msg,
                detail: e.type,
                field: field,
                ctx: JSON.stringify(e.ctx),
            }

            storeErrors.push(validationError);

            if (target.type === CustomizationObjectType.Roi) {
                // create a copy for global roi if there's a matching one (and one hasn't been made already)
                const globalRoi = Object.values(state.globalRoiCustomizations.entities).find(gr => gr?.coveredRois.includes(target!.id));
                if (globalRoi && !storeErrors.find(e => e.id === globalRoi.id)) {
                    storeErrors.push({ ...validationError, id: globalRoi.id });
                }
            }
        }
    });

    return storeErrors;
}

const getTargetType = (loc: string | number): CustomizationObjectType => {
    if (!isString(loc)) {
        return CustomizationObjectType.None;
    }

    switch (loc) {
        case 'rois':
            return CustomizationObjectType.Roi;
        case 'metadata':
            return CustomizationObjectType.Metadata;
        case 'files':
            return CustomizationObjectType.CustomizationOutput;
        case 'customizations':
            return CustomizationObjectType.CustomizationBase;
        case 'triggers':
            return CustomizationObjectType.TriggerRule;
        case 'body':
            return CustomizationObjectType.Model;
        case 'fma_id':
            return CustomizationObjectType.FmaId;
        case 'physical_properties':
            return CustomizationObjectType.PhysicalProperties;
        default:
            return CustomizationObjectType.None;
    }
}

const getTargetObject = (state: StoreState, type: CustomizationObjectType, jsonCursor: any, target: { id: string, type: CustomizationObjectType } | undefined, cursorPath: string) => {
    const currentId = jsonCursor[UI_ID_ATTRIBUTE];
    switch (type) {
        case CustomizationObjectType.Model:
            {
                const model = state.segmentationModels.entities[currentId];
                if (model === undefined) { throw new Error(`Could not find matching segmentation model for validation error json node ${cursorPath}`); }
                return { id: model.id, type };
            }

        case CustomizationObjectType.TriggerRule:
            {
                if (jsonCursor['action'] !== undefined) {
                    const aeTitleRule = state.aeTitleRules.entities[currentId];
                    if (aeTitleRule) {
                        return { id: aeTitleRule.id, type: CustomizationObjectType.AeTitleRule };
                    }
                } else if (jsonCursor['dicom_attributes'] !== undefined) {
                    const dicomRule = state.dicomRules.entities[currentId];
                    if (dicomRule) {
                        return { id: dicomRule.id, type: CustomizationObjectType.DicomRule };
                    }
                }

                throw new Error(`Could not retrieve either an AE title rule or DICOM rule for ${cursorPath}`);
            }

        case CustomizationObjectType.CustomizationBase:
            {
                const customizationBase = state.customizationBases.entities[currentId];
                if (customizationBase === undefined) { throw new Error(`Could not find matching customization base for validation error json node ${cursorPath}`); }
                return { id: customizationBase.id, type };
            }

        case CustomizationObjectType.CustomizationOutput:
            {
                const customizationOutput = state.customizationOutputs.entities[currentId];
                if (customizationOutput === undefined) { throw new Error(`Could not find matching customization output for validation error json node ${cursorPath}`); }
                return { id: customizationOutput.id, type };
            }

        case CustomizationObjectType.Metadata:
            {
                const metadata = state.modelCustomizationsMetadata.entities[currentId];
                if (metadata === undefined) { throw new Error(`Could not find matching metadata entry for validation error json node ${cursorPath}`); }
                return { id: metadata.id, type };
            }

        case CustomizationObjectType.Roi:
            {
                const roi = state.roiCustomizations.entities[currentId];
                if (roi === undefined) { throw new Error(`Could not find matching roi customization for validation error json node ${cursorPath}`); }
                return { id: roi.id, type };
            }

        case CustomizationObjectType.PhysicalProperties:
            {
                // return the previous roi but with the new PhysicalProperties type
                const roi = target && target.type === CustomizationObjectType.Roi ? state.roiCustomizations.entities[target.id] : undefined;
                if (roi === undefined) { throw new Error(`Could not find matching roi customization for validation error json node ${cursorPath}`); }
                return { id: roi.id, type };
            }

        default:
            throw new Error(`No valid type given for getTargetObject (${type})`);
    }
}

const convertDaemonValidationErrorsToStoreObjects = (daemonSessionId: string, error: BackendValidationError): DaemonValidationError[] => {
    const storeErrors = error.validationErrors.map((error, index) => ({
        id: `${daemonSessionId}-${index}`,
        field: error.loc !== undefined && isArray(error.loc) ? isString(error.loc[error.loc.length - 1]) ? error.loc[error.loc.length - 1] as string : undefined : undefined,
        message: error.msg,
        detail: error.type,
        ctx: JSON.stringify(error.ctx)
    }));

    return storeErrors;
}


/// unit test helper export functions
// these functions just export existing functions that are otherwise internal to this file
// these should NOT be used outside of unit tests

export const _unitTest_calculateFormValidationErrorsForCustomizationOutput = (state: StoreState, outputId: string): FormValidationErrorCalculationResults => {
    return calculateFormValidationErrorsForCustomizationOutput(state, outputId);
}

export const _unitTest_validateDicomRuleHasAtLeastOneAttribute = (dicomRule: DicomRule): DicomAttributeValidationResults => {
    return validateDicomRuleHasAtLeastOneAttribute(dicomRule);
}

export const _unitTest_validateDicomAttributeNamesAreNotEmpty = (dicomAttributeRules: DicomAttributeRule[]): DicomAttributeValidationResults => {
    return validateDicomAttributeNamesAreNotEmpty(dicomAttributeRules);
}

export const _unitTest_validataAllDicomAttributeNamesHaveNoDuplicates = (dicomAttributeRules: DicomAttributeRule[]): DicomAttributeValidationResults => {
    return validateAllDicomAttributeNamesHaveNoDuplicates(dicomAttributeRules);
}


export const _unitTest_viewModelAdapters = {
    modelAdapter,
    customizationBaseAdapter,
    customizationOutputAdapter,
    modelCustomizationsMetadataAdapter,
    roiCustomizationAdapter,
    globalRoiCustomizationAdapter,
    aeTitleRuleAdapter,
    dicomRuleAdapter,
    dicomAttributeRuleAdapter,
    customizationValidationErrorAdapter,
    formValidationErrorAdapter,
    daemonValidationErrorAdapter,
}
